import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase, getOrCreateInstitution, listStudents, createStudent, deactivateStudent, sendFormsBatch, listResponses, updateStudentConsent, sendTermsBatch } from "./supabase";


// ── MOCK DATA ──────────────────────────────────────────────────────────────
const SCHOOLS = ["E.M. João Pessoa", "E.M. Santos Dumont", "EMEF Monteiro Lobato"];
const YEARS   = ["1º Ano", "2º Ano", "3º Ano", "4º Ano", "5º Ano", "6º Ano", "7º Ano", "8º Ano", "9º Ano"];
const CLASSES  = ["A", "B", "C", "D"];

const INITIAL_STUDENTS = [
  { id: 1, name: "Ana Beatriz Souza",    age: 14, year: "8º Ano", cls: "A", school: SCHOOLS[0], risk: "alto",   lastResp: "15/02/2025", alerts: 3 },
  { id: 2, name: "Carlos Eduardo Lima",  age: 12, year: "6º Ano", cls: "B", school: SCHOOLS[0], risk: "médio",  lastResp: "14/02/2025", alerts: 1 },
  { id: 3, name: "Marina Alves Costa",   age: 15, year: "9º Ano", cls: "A", school: SCHOOLS[1], risk: "baixo",  lastResp: "16/02/2025", alerts: 0 },
  { id: 4, name: "Pedro Henrique Nunes", age: 13, year: "7º Ano", cls: "C", school: SCHOOLS[0], risk: "alto",   lastResp: "13/02/2025", alerts: 2 },
  { id: 5, name: "Julia Ferreira",       age: 11, year: "5º Ano", cls: "A", school: SCHOOLS[2], risk: "baixo",  lastResp: "17/02/2025", alerts: 0 },
  { id: 6, name: "Lucas Mendes",         age: 14, year: "8º Ano", cls: "B", school: SCHOOLS[1], risk: "médio",  lastResp: "12/02/2025", alerts: 1 },
  { id: 7, name: "Sofia Rodrigues",      age: 16, year: "9º Ano", cls: "D", school: SCHOOLS[0], risk: "alto",   lastResp: "10/02/2025", alerts: 4 },
  { id: 8, name: "Gabriel Pereira",      age: 12, year: "6º Ano", cls: "A", school: SCHOOLS[2], risk: "baixo",  lastResp: "18/02/2025", alerts: 0 },
];

// ── META DAS ESCALAS (usado no dashboard, alertas e respostas) ──────────
const ESCALA_META = {
  who5:  { title: "WHO-5",  subtitle: "Índice de Bem-Estar",     max: 25, icon: "🌱" },
  swls:  { title: "SWLS",   subtitle: "Satisfação com a Vida",   max: 35, icon: "⭐" },
  gad7:  { title: "GAD-7",  subtitle: "Rastreio de Ansiedade",   max: 21, icon: "💭" },
  eps10: { title: "EPS-10", subtitle: "Estresse Percebido",      max: 40, icon: "⚡" },
};

// ── ALERTAS DERIVADOS DAS RESPOSTAS ───────────────────────────────────────
const RISK_ORDER = { alto: 3, "médio": 2, baixo: 1 };
const READ_ALERTS_KEY = "ouvy_alerts_read";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("pt-BR");
}

function loadReadAlerts() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_ALERTS_KEY) || "[]")); }
  catch { return new Set(); }
}

function persistReadAlerts(set) {
  try { localStorage.setItem(READ_ALERTS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

/**
 * Enriquece alunos com risco, último formulário e nº de alertas a partir das
 * respostas reais. Risco = maior nível entre a resposta mais recente de cada
 * escala (responses já vêm ordenadas por answered_at desc).
 */
function enrichStudents(students, responses) {
  const byStudent = {};
  for (const r of responses) {
    (byStudent[r.student_id] = byStudent[r.student_id] || []).push(r);
  }
  return students.map(s => {
    const list = byStudent[s.id] || [];
    const latestByScale = {};
    for (const r of list) if (!latestByScale[r.escala_id]) latestByScale[r.escala_id] = r;
    let risk = "baixo";
    for (const r of Object.values(latestByScale)) {
      if ((RISK_ORDER[r.nivel_risco] || 0) > RISK_ORDER[risk]) risk = r.nivel_risco;
    }
    return {
      ...s,
      risk,
      lastResp: fmtDate(list[0]?.answered_at),
      alerts: list.filter(r => r.nivel_risco === "alto" || r.nivel_risco === "médio").length,
    };
  });
}

/** Uma resposta com risco médio/alto vira um alerta. */
function buildAlerts(responses, readSet) {
  return responses
    .filter(r => r.nivel_risco === "alto" || r.nivel_risco === "médio")
    .map(r => ({
      id: r.id,
      studentId: r.student_id,
      student: r.students?.name || "Aluno",
      type: `${r.label_resultado || "Risco " + r.nivel_risco} · ${ESCALA_META[r.escala_id]?.title || r.escala_id}`,
      date: fmtDate(r.answered_at),
      priority: r.nivel_risco === "alto" ? "alta" : "média",
      read: readSet.has(r.id),
    }));
}

// ── ESCALAS CLÍNICAS ─────────────────────────────────────────────────────
const ESCALAS = {
  who5: {
    id: "who5",
    title: "WHO-5 — Índice de Bem-Estar",
    desc: "Indique para cada afirmação a que se aproxima mais do modo como você se tem sentido nas últimas duas semanas.",
    instrucao: "Durante as últimas duas semanas...",
    tipo: "likert",
    opcoes: [
      { label: "Todo o tempo", valor: 5 },
      { label: "A maior parte do tempo", valor: 4 },
      { label: "Mais de metade do tempo", valor: 3 },
      { label: "Menos de metade do tempo", valor: 2 },
      { label: "Algum tempo", valor: 1 },
      { label: "Nunca", valor: 0 },
    ],
    questoes: [
      "Tenho me sentido alegre e de bom humor.",
      "Tenho me sentido calmo/a e relaxado/a.",
      "Tenho me sentido ativo/a e energizado/a.",
      "Acordei me sentindo revigorado/a e descansado/a.",
      "Minha vida diária tem sido repleta de coisas que me interessam.",
    ],
    pontuacao: (soma) => {
      const pct = soma * 4;
      if (soma < 13) return { nivel: "alto", label: "Pobre bem-estar", detalhe: `${soma}/25 (${pct}%) — Sugere avaliação clínica para depressão` };
      if (soma <= 17) return { nivel: "médio", label: "Bem-estar moderado", detalhe: `${soma}/25 (${pct}%)` };
      return { nivel: "baixo", label: "Bom bem-estar", detalhe: `${soma}/25 (${pct}%)` };
    },
  },
  swls: {
    id: "swls",
    title: "SWLS-BP — Satisfação com a Vida",
    desc: "Abaixo você encontrará cinco afirmações com as quais pode ou não concordar. Indique o quanto concorda ou discorda com cada uma.",
    instrucao: "Usando a escala de 1 a 7, marque sua resposta:",
    tipo: "likert",
    opcoes: [
      { label: "Concordo totalmente", valor: 7 },
      { label: "Concordo", valor: 6 },
      { label: "Concordo ligeiramente", valor: 5 },
      { label: "Nem concordo nem discordo", valor: 4 },
      { label: "Discordo ligeiramente", valor: 3 },
      { label: "Discordo", valor: 2 },
      { label: "Discordo totalmente", valor: 1 },
    ],
    questoes: [
      "Na maioria dos aspectos, minha vida é próxima ao meu ideal.",
      "As condições da minha vida são excelentes.",
      "Estou satisfeito(a) com minha vida.",
      "Dentro do possível, tenho conseguido as coisas importantes que quero da vida.",
      "Se pudesse viver uma segunda vez, não mudaria quase nada na minha vida.",
    ],
    pontuacao: (soma) => {
      if (soma >= 31) return { nivel: "baixo", label: "Extremamente Satisfeito", detalhe: `${soma}/35` };
      if (soma >= 26) return { nivel: "baixo", label: "Satisfeito", detalhe: `${soma}/35` };
      if (soma >= 21) return { nivel: "baixo", label: "Ligeiramente Satisfeito", detalhe: `${soma}/35` };
      if (soma === 20) return { nivel: "médio", label: "Neutro", detalhe: `${soma}/35` };
      if (soma >= 15) return { nivel: "médio", label: "Ligeiramente Insatisfeito", detalhe: `${soma}/35` };
      if (soma >= 10) return { nivel: "alto", label: "Insatisfeito", detalhe: `${soma}/35` };
      return { nivel: "alto", label: "Extremamente Insatisfeito", detalhe: `${soma}/35` };
    },
  },
  gad7: {
    id: "gad7",
    title: "GAD-7 — Ansiedade Generalizada",
    desc: "Durante as últimas 2 semanas, com que frequência você foi incomodado/a pelos problemas abaixo?",
    instrucao: "Nas últimas 2 semanas, com que frequência...",
    tipo: "likert",
    opcoes: [
      { label: "Nenhuma vez", valor: 0 },
      { label: "Vários dias", valor: 1 },
      { label: "Mais da metade dos dias", valor: 2 },
      { label: "Quase todos os dias", valor: 3 },
    ],
    questoes: [
      "Sentir-se nervoso/a, ansioso/a ou no limite.",
      "Não conseguir parar ou controlar as preocupações.",
      "Preocupar-se demais com diferentes coisas.",
      "Dificuldade em relaxar.",
      "Ficar tão agitado/a que se torna difícil ficar parado/a.",
      "Ficar facilmente irritado/a ou irritável.",
      "Sentir medo de que algo terrível possa acontecer.",
    ],
    pontuacao: (soma) => {
      if (soma <= 4)  return { nivel: "baixo", label: "Ansiedade Mínima", detalhe: `${soma}/21` };
      if (soma <= 9)  return { nivel: "médio", label: "Ansiedade Leve", detalhe: `${soma}/21` };
      if (soma <= 14) return { nivel: "alto",  label: "Ansiedade Moderada", detalhe: `${soma}/21` };
      return { nivel: "alto", label: "Ansiedade Grave", detalhe: `${soma}/21` };
    },
  },
  eps10: {
    id: "eps10",
    title: "EPS-10 — Estresse Percebido",
    desc: "As questões abaixo perguntam a respeito dos seus sentimentos e pensamentos durante os últimos 30 dias. Indique a frequência com que você se sentiu ou pensou a respeito de cada situação.",
    instrucao: "Nos últimos 30 dias, com que frequência...",
    tipo: "likert",
    opcoes: [
      { label: "Nunca", valor: 0 },
      { label: "Quase Nunca", valor: 1 },
      { label: "Às Vezes", valor: 2 },
      { label: "Pouco Frequente", valor: 3 },
      { label: "Muito Frequente", valor: 4 },
    ],
    invertidos: [3, 4, 6, 7], // índices 0-based das questões 4,5,7,8
    questoes: [
      "Você ficou aborrecido por causa de algo que aconteceu inesperadamente?",
      "Você sentiu que foi incapaz de controlar coisas importantes na sua vida?",
      "Você esteve nervoso ou estressado?",
      "Você esteve confiante em sua capacidade de lidar com seus problemas pessoais?",
      "Você sentiu que as coisas aconteceram da maneira que você esperava?",
      "Você achou que não conseguiria lidar com todas as coisas que tinha por fazer?",
      "Você foi capaz de controlar irritações na sua vida?",
      "Você sentiu que todos os aspectos de sua vida estavam sob controle?",
      "Você esteve bravo por causa de coisas que estiveram fora de seu controle?",
      "Você sentiu que os problemas acumularam tanto que você não conseguiria resolvê-los?",
    ],
    pontuacao: (soma) => {
      if (soma <= 13) return { nivel: "baixo", label: "Estresse Baixo", detalhe: `${soma}/40` };
      if (soma <= 26) return { nivel: "médio", label: "Estresse Moderado", detalhe: `${soma}/40` };
      return { nivel: "alto", label: "Estresse Elevado", detalhe: `${soma}/40` };
    },
  },
};
const INITIAL_FORMS = [
  { id: "who5", escalaId: "who5", title: "WHO-5 — Índice de Bem-Estar",       responses: 34, created: "01/02/2025", active: true },
  { id: "swls", escalaId: "swls", title: "SWLS-BP — Satisfação com a Vida",   responses: 18, created: "01/02/2025", active: true },
  { id: "gad7", escalaId: "gad7", title: "GAD-7 — Ansiedade Generalizada",    responses: 27, created: "01/02/2025", active: true },
  { id: "eps10", escalaId: "eps10", title: "EPS-10 — Estresse Percebido",         responses: 15, created: "01/02/2025", active: true },
];

// ── STYLES ─────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --sage:       #1E7B78;
    --sage-light: #E6F4F3;
    --sage-mid:   #9DD0CE;
    --cream:      #F7FAFA;
    --warm:       #EDF4F4;
    --ink:        #0D2626;
    --ink-mid:    #3A5050;
    --ink-light:  #7A9898;
    --amber:      #D4822A;
    --rose:       #C25B5B;
    --sky:        #2E8FA8;
    --border:     #D8E8E8;
    --white:      #FFFFFF;
    --sidebar-w:  256px;
    --radius:     12px;
    --shadow:     0 1px 3px rgba(13,38,38,.06), 0 4px 16px rgba(13,38,38,.08);
    --shadow-lg:  0 8px 32px rgba(13,38,38,.14);
  }

  body { font-family: 'DM Sans', sans-serif; background: var(--cream); color: var(--ink); }
  /* ── LOGIN ───────────────────────────────────────────────── */
  .login-page {
    min-height: 100vh; display: flex;
    background: var(--cream); position: relative; overflow: hidden;
  }
  .login-left {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 60px;
  }
  .login-right {
    width: 520px; background: var(--ink);
    display: flex; align-items: center; justify-content: center;
    padding: 60px 52px;
  }
  .login-brand { margin-bottom: 48px; }
  .login-brand-name {
    font-family: 'Fraunces', serif; font-size: 36px;
    color: var(--ink); font-weight: 500; display: flex; align-items: center; gap: 14px;
  }
  .login-tagline {
    font-size: 15px; color: var(--ink-mid); margin-top: 10px;
    line-height: 1.6; max-width: 380px;
  }
  .login-stats { display: flex; gap: 40px; margin-top: 48px; }
  .login-stat-num {
    font-family: 'Fraunces', serif; font-size: 32px; color: var(--sage); font-weight: 700;
  }
  .login-stat-label { font-size: 12px; color: var(--ink-mid); margin-top: 2px; }
  .login-deco {
    position: absolute; left: 60%; top: 50%; transform: translateY(-50%);
    width: 340px; height: 340px; border-radius: 50%;
    background: radial-gradient(circle, rgba(30,123,120,.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .login-box { width: 100%; max-width: 360px; }
  .login-title {
    font-family: 'Fraunces', serif; font-size: 26px; font-weight: 500;
    color: var(--cream); margin-bottom: 6px;
  }
  .login-subtitle { font-size: 14px; color: var(--sage-mid); margin-bottom: 36px; }
  .login-form .form-group { margin-bottom: 20px; }
  .login-form label { color: var(--sage-mid); }
  .login-input {
    width: 100%; padding: 12px 16px; border: 1.5px solid rgba(255,255,255,.15);
    border-radius: 10px; font-size: 15px; font-family: 'DM Sans', sans-serif;
    color: var(--cream); outline: none; transition: border-color .15s, box-shadow .15s;
    background: rgba(255,255,255,.08);
  }
  .login-input::placeholder { color: rgba(255,255,255,.35); }
  .login-input:focus { border-color: var(--sage-mid); box-shadow: 0 0 0 3px rgba(30,123,120,.25); }
  .login-btn {
    width: 100%; padding: 13px; background: var(--sage); color: white;
    border: none; border-radius: 10px; font-size: 15px; font-weight: 600;
    font-family: 'DM Sans', sans-serif; cursor: pointer; transition: background .15s; margin-top: 8px;
  }
  .login-btn:hover:not(:disabled) { background: #175f5d; }
  .login-btn:disabled { opacity: .65; cursor: not-allowed; }
  .login-error {
    background: #fee8e8; border: 1px solid #f9c2c2; color: var(--rose);
    padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 18px;
  }
  .login-info {
    background: rgba(30,123,120,.2); border-left: 3px solid var(--sage-mid);
    padding: 10px 14px; border-radius: 8px; font-size: 13px;
    color: var(--sage-mid); margin-bottom: 18px; line-height: 1.5;
  }
  .login-footer { font-size: 12px; color: var(--sage-mid); text-align: center; margin-top: 32px; }
  @media (max-width: 900px) {
    .login-left { display: none; }
    .login-right { width: 100%; padding: 40px 24px; }
  }


  .app { display: flex; min-height: 100vh; }

  /* SIDEBAR */
  .sidebar {
    width: var(--sidebar-w); min-height: 100vh; background: var(--ink);
    display: flex; flex-direction: column; position: fixed; left: 0; top: 0; z-index: 100;
    padding: 0;
  }
  .sidebar-logo {
    height: 64px;
    border-bottom: 1px solid var(--border);
    background: var(--white);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .logo-icon { flex-shrink: 0; }
  .logo-text { font-family: 'Fraunces', serif; font-size: 22px; color: var(--cream); line-height: 1.1; font-weight: 500; letter-spacing: .01em; }
  .logo-sub { font-size: 10px; color: var(--ink-light); letter-spacing: .1em; text-transform: uppercase; margin-top: 1px; }
  .sidebar-nav { flex: 1; padding: 16px 12px; display: flex; flex-direction: column; gap: 2px; }
  .nav-section { font-size: 10px; color: var(--ink-light); letter-spacing: .1em; text-transform: uppercase; padding: 12px 12px 6px; }
  .nav-item {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px;
    cursor: pointer; color: rgba(250,247,242,.6); font-size: 14px; font-weight: 400;
    transition: all .18s ease; border: none; background: none; width: 100%; text-align: left;
  }
  .nav-item:hover { background: rgba(255,255,255,.06); color: var(--cream); }
  .nav-item.active { background: var(--sage); color: var(--cream); font-weight: 500; }
  .nav-icon { font-size: 16px; width: 20px; text-align: center; }
  .sidebar-footer { padding: 16px 12px; border-top: 1px solid rgba(255,255,255,.08); }
  .user-pill {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px;
    background: rgba(255,255,255,.05);
  }
  .user-avatar {
    width: 32px; height: 32px; border-radius: 50%; background: var(--sage);
    display: flex; align-items: center; justify-content: center; font-size: 13px; color: white; font-weight: 600;
  }
  .user-name { font-size: 13px; color: var(--cream); font-weight: 500; }
  .user-role { font-size: 11px; color: var(--ink-light); }

  /* MAIN */
  .main { margin-left: var(--sidebar-w); flex: 1; display: flex; flex-direction: column; min-height: 100vh; }

  .page-title { font-family: 'Fraunces', serif; font-size: 26px; font-weight: 500; color: var(--ink); margin-bottom: 24px; }

  /* CONTENT */
  .content { padding: 32px 36px; flex: 1; }

  /* NOTIFICATION FAB */
  .notif-fab-wrap { position: fixed; bottom: 28px; right: 28px; z-index: 300; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
  .notif-fab {
    width: 54px; height: 54px; border-radius: 50%; background: var(--sage); border: none;
    cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 22px;
    box-shadow: 0 4px 18px rgba(30,123,120,.35); transition: transform .2s, box-shadow .2s;
    position: relative;
  }
  .notif-fab:hover { transform: scale(1.08); box-shadow: 0 6px 26px rgba(30,123,120,.45); }
  .notif-fab-count {
    position: absolute; top: -3px; right: -3px;
    min-width: 20px; height: 20px; border-radius: 10px; padding: 0 5px;
    background: var(--rose); color: white; font-size: 11px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    border: 2px solid white;
  }
  .notif-panel {
    background: white; border: 1px solid var(--border); border-radius: 14px;
    box-shadow: 0 8px 32px rgba(13,38,38,.14); width: 320px; overflow: hidden;
    animation: slideUp .2s ease;
  }
  .notif-panel-header {
    padding: 14px 18px 12px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .notif-panel-title { font-family:'Fraunces',serif; font-size:15px; font-weight:500; }
  .notif-panel-body { max-height: 340px; overflow-y: auto; }
  .notif-item {
    display: flex; align-items: flex-start; gap: 12px; padding: 13px 18px;
    border-bottom: 1px solid var(--border); transition: background .12s;
  }
  .notif-item:last-child { border-bottom: none; }
  .notif-item:hover { background: var(--cream); }
  .notif-item-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 5px; }
  .notif-item-dot.unread { background: var(--rose); }
  .notif-item-dot.read   { background: var(--border); }
  .notif-item-name  { font-size: 13px; font-weight: 600; color: var(--ink); }
  .notif-item-type  { font-size: 12px; color: var(--ink-mid); margin-top: 1px; }
  .notif-item-date  { font-size: 11px; color: var(--ink-light); margin-top: 3px; }
  .notif-empty { padding: 28px; text-align: center; font-size: 13px; color: var(--ink-light); }

  /* CARDS */
  .card {
    background: var(--white); border: 1px solid var(--border); border-radius: var(--radius);
    box-shadow: var(--shadow); overflow: hidden;
  }
  .card-header {
    padding: 20px 24px 16px; display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid var(--border);
  }
  .card-title { font-family: 'Fraunces', serif; font-size: 17px; font-weight: 500; }
  .card-body { padding: 20px 24px; }

  /* STAT CARDS */
  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
  .stat-card {
    background: var(--white); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 20px 22px; box-shadow: var(--shadow); position: relative; overflow: hidden;
  }
  .stat-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  }
  .stat-card.alto::before  { background: var(--rose); }
  .stat-card.médio::before { background: var(--amber); }
  .stat-card.baixo::before { background: var(--sage); }
  .stat-card.total::before { background: var(--sage); }
  .stat-num { font-family: 'Fraunces', serif; font-size: 36px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
  .stat-label { font-size: 13px; color: var(--ink-mid); }
  .stat-icon { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); font-size: 28px; opacity: .12; }

  /* TABLES */
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; padding: 10px 16px; font-size: 11px; font-weight: 600;
       letter-spacing: .06em; text-transform: uppercase; color: var(--ink-light);
       background: var(--cream); border-bottom: 1px solid var(--border); }
  td { padding: 13px 16px; font-size: 14px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: var(--cream); }

  /* RISK BADGE */
  .risk {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 500;
  }
  .risk.alto  { background: #FEE8E8; color: var(--rose); }
  .risk.médio { background: #FEF3E2; color: var(--amber); }
  .risk.baixo { background: #E8F3E9; color: var(--sage); }
  .risk-dot { width: 6px; height: 6px; border-radius: 50%; }
  .risk.alto  .risk-dot { background: var(--rose); }
  .risk.médio .risk-dot { background: var(--amber); }
  .risk.baixo .risk-dot { background: var(--sage); }

  /* PRIORITY */
  .priority.alta  { color: var(--rose); font-weight: 600; }
  .priority.média { color: var(--amber); font-weight: 600; }

  /* BUTTONS */
  .btn {
    display: inline-flex; align-items: center; gap: 6px; padding: 9px 18px;
    border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer;
    border: none; transition: all .15s; font-family: 'DM Sans', sans-serif;
  }
  .btn-primary { background: var(--sage); color: white; }
  .btn-primary:hover { background: #5a7860; }
  .btn-outline { background: transparent; color: var(--ink-mid); border: 1px solid var(--border); }
  .btn-outline:hover { background: var(--cream); }
  .btn-danger { background: transparent; color: var(--rose); border: 1px solid #f9c2c2; }
  .btn-danger:hover { background: #FEE8E8; }
  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-icon { padding: 8px; border-radius: 8px; background: transparent; border: 1px solid var(--border); cursor: pointer; transition: all .15s; }
  .btn-icon:hover { background: var(--cream); }

  /* FILTER BAR */
  .filter-bar { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
  .filter-select, .filter-input {
    padding: 8px 14px; border: 1px solid var(--border); border-radius: 8px;
    font-size: 13px; background: white; color: var(--ink); font-family: 'DM Sans', sans-serif;
    outline: none; transition: border-color .15s;
  }
  .filter-input { min-width: 220px; }
  .filter-select:focus, .filter-input:focus { border-color: var(--sage); }

  /* GRID 2 */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }

  /* ALERT ITEM */
  .alert-item {
    display: flex; align-items: flex-start; gap: 14px; padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }
  .alert-item:last-child { border-bottom: none; }
  .alert-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
  .alert-icon.alta  { background: #FEE8E8; }
  .alert-icon.média { background: #FEF3E2; }
  .alert-body { flex: 1; }
  .alert-name { font-size: 14px; font-weight: 500; }
  .alert-type { font-size: 13px; color: var(--ink-mid); margin-top: 1px; }
  .alert-date { font-size: 12px; color: var(--ink-light); margin-top: 4px; }
  .unread-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--sage); flex-shrink: 0; margin-top: 6px; }

  /* MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(28,35,32,.4); z-index: 200;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    backdrop-filter: blur(2px);
    animation: fadeIn .2s ease;
  }
  .modal {
    background: white; border-radius: 16px; box-shadow: var(--shadow-lg);
    width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto;
    animation: slideUp .2s ease;
  }
  .modal-header {
    padding: 24px 28px 20px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .modal-title { font-family: 'Fraunces', serif; font-size: 20px; font-weight: 500; }
  .modal-body { padding: 24px 28px; }
  .modal-footer { padding: 16px 28px 24px; display: flex; justify-content: flex-end; gap: 10px; }

  /* FORM ELEMENTS */
  .form-group { margin-bottom: 18px; }
  .form-label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; color: var(--ink-mid); }
  .form-input, .form-select, .form-textarea {
    width: 100%; padding: 10px 14px; border: 1px solid var(--border); border-radius: 8px;
    font-size: 14px; font-family: 'DM Sans', sans-serif; color: var(--ink); outline: none;
    transition: border-color .15s; background: white;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus { border-color: var(--sage); box-shadow: 0 0 0 3px rgba(107,143,113,.12); }
  .form-textarea { min-height: 80px; resize: vertical; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  /* LINK BOX */
  .link-box {
    background: var(--sage-light); border: 1px solid var(--sage-mid);
    border-radius: 10px; padding: 16px 18px; display: flex; align-items: center; gap: 12px;
  }
  .link-url { font-size: 13px; color: var(--sage); font-family: monospace; flex: 1; word-break: break-all; }

  /* CHART BAR */
  .bar-chart { display: flex; flex-direction: column; gap: 10px; }
  .bar-row { display: flex; align-items: center; gap: 10px; }
  .bar-label { font-size: 12px; color: var(--ink-mid); width: 70px; text-align: right; flex-shrink: 0; }
  .bar-track { flex: 1; height: 10px; background: var(--warm); border-radius: 10px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 10px; transition: width .6s ease; }
  .bar-val { font-size: 12px; color: var(--ink-light); width: 24px; flex-shrink: 0; }

  /* FORM PREVIEW */
  .q-card { background: var(--cream); border: 1px solid var(--border); border-radius: 10px; padding: 16px 18px; margin-bottom: 12px; }
  .q-num { font-size: 11px; color: var(--ink-light); margin-bottom: 4px; }
  .q-text { font-size: 14px; font-weight: 500; margin-bottom: 10px; }
  .scale-row { display: flex; gap: 8px; }
  .scale-btn {
    width: 36px; height: 36px; border-radius: 50%; border: 1.5px solid var(--border);
    background: white; cursor: pointer; font-size: 13px; font-weight: 600;
    display: flex; align-items: center; justify-content: center; transition: all .15s;
  }
  .scale-btn:hover { border-color: var(--sage); background: var(--sage-light); }
  .opt-row { display: flex; flex-direction: column; gap: 6px; }
  .opt-btn {
    padding: 8px 14px; border: 1.5px solid var(--border); border-radius: 8px;
    background: white; cursor: pointer; font-size: 13px; text-align: left; transition: all .15s;
  }
  .opt-btn:hover { border-color: var(--sage); background: var(--sage-light); }

  /* TABS */
  .tabs { display: flex; border-bottom: 1px solid var(--border); margin-bottom: 24px; }
  .tab-btn {
    padding: 10px 20px; border: none; background: none; cursor: pointer;
    font-size: 14px; color: var(--ink-light); font-family: 'DM Sans', sans-serif;
    border-bottom: 2px solid transparent; transition: all .15s; margin-bottom: -1px;
  }
  .tab-btn.active { color: var(--sage); border-bottom-color: var(--sage); font-weight: 600; }
  .tab-btn:hover { color: var(--ink); }

  /* SETTINGS */
  .setting-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 0; border-bottom: 1px solid var(--border);
  }
  .setting-row:last-child { border-bottom: none; }
  .setting-info h4 { font-size: 14px; font-weight: 500; }
  .setting-info p  { font-size: 13px; color: var(--ink-light); margin-top: 2px; }
  .toggle {
    width: 44px; height: 24px; border-radius: 12px; border: none; cursor: pointer;
    position: relative; transition: background .2s;
  }
  .toggle.on  { background: var(--sage); }
  .toggle.off { background: var(--border); }
  .toggle::after {
    content: ''; position: absolute; width: 18px; height: 18px; border-radius: 50%;
    background: white; top: 3px; transition: left .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2);
  }
  .toggle.on::after  { left: 23px; }
  .toggle.off::after { left: 3px; }

  /* EMPTY STATE */
  .empty { text-align: center; padding: 48px 24px; }
  .empty-icon { font-size: 40px; margin-bottom: 12px; }
  .empty-text { font-size: 15px; color: var(--ink-mid); }

  /* BREADCRUMB */
  .breadcrumb { display: flex; align-items: center; gap: 6px; margin-bottom: 20px; font-size: 13px; color: var(--ink-light); }
  .breadcrumb span { cursor: pointer; }
  .breadcrumb span:hover { color: var(--sage); }

  /* DETAIL STUDENT */
  .detail-header { display: flex; align-items: center; gap: 18px; margin-bottom: 24px; }
  .detail-avatar {
    width: 64px; height: 64px; border-radius: 50%; background: var(--sage-light);
    border: 2px solid var(--sage-mid); display: flex; align-items: center; justify-content: center;
    font-family: 'Fraunces', serif; font-size: 24px; color: var(--sage);
  }
  .detail-name { font-family: 'Fraunces', serif; font-size: 22px; }
  .detail-meta { font-size: 13px; color: var(--ink-mid); margin-top: 3px; }

  /* MINI DONUT (CSS only) */
  .donut-wrap { position: relative; width: 90px; height: 90px; }
  .donut-circle { transform: rotate(-90deg); }

  /* RESPONSIVE tweaks */
  @media (max-width: 900px) {
    .stats-grid { grid-template-columns: repeat(2, 1fr); }
    .grid-2 { grid-template-columns: 1fr; }
    .grid-3 { grid-template-columns: 1fr; }
  }

  @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
  @keyframes slideUp { from { transform: translateY(16px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }

  .fade-in { animation: fadeIn .3s ease; }

  /* TOAST */
  .toast {
    position: fixed; bottom: 24px; right: 24px; z-index: 999;
    background: var(--ink); color: white; padding: 12px 20px; border-radius: 10px;
    font-size: 14px; box-shadow: var(--shadow-lg); animation: slideUp .25s ease;
    display: flex; align-items: center; gap: 8px;
  }

  /* ── RESPONSES PAGE ─────────────────────────────────────────── */
  .resp-summary {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px;
  }
  .resp-stat {
    background: var(--white); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 18px 20px; position: relative; overflow: hidden;
  }
  .resp-stat::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; }
  .resp-stat.total::before  { background: var(--sage); }
  .resp-stat.alto::before   { background: var(--rose); }
  .resp-stat.medio::before  { background: var(--amber); }
  .resp-stat.baixo::before  { background: #4caf88; }
  .resp-stat-num { font-family:'Fraunces',serif; font-size:30px; font-weight:700; line-height:1; }
  .resp-stat-lbl { font-size:12px; color:var(--ink-mid); margin-top:4px; }

  .resp-cards {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px;
  }
  .resp-card {
    background: var(--white); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 20px; cursor: pointer; transition: box-shadow .18s, transform .18s;
    display: flex; flex-direction: column; gap: 14px;
  }
  .resp-card:hover { box-shadow: 0 6px 24px rgba(13,38,38,.10); transform: translateY(-2px); }
  .resp-card-top { display: flex; align-items: center; gap: 12px; }
  .resp-avatar {
    width: 40px; height: 40px; border-radius: 50%; background: var(--sage-light);
    display: flex; align-items: center; justify-content: center;
    font-family:'Fraunces',serif; font-size:15px; color:var(--sage); flex-shrink:0;
  }
  .resp-student-name { font-size:14px; font-weight:600; color:var(--ink); }
  .resp-student-meta { font-size:12px; color:var(--ink-light); margin-top:2px; }
  .resp-escala-badge {
    margin-left:auto; font-size:11px; font-weight:600; padding:3px 10px;
    border-radius:20px; background:var(--sage-light); color:var(--sage); white-space:nowrap;
  }

  .resp-score-area { display:flex; flex-direction:column; gap:6px; }
  .resp-score-header { display:flex; align-items:center; justify-content:space-between; }
  .resp-score-label { font-size:13px; color:var(--ink-mid); }
  .resp-score-val { font-family:'Fraunces',serif; font-size:16px; font-weight:600; color:var(--ink); }
  .resp-bar-track { height:7px; background:var(--warm); border-radius:10px; overflow:hidden; }
  .resp-bar-fill  { height:100%; border-radius:10px; transition:width .5s ease; }
  .resp-bar-fill.alto  { background:var(--rose); }
  .resp-bar-fill.médio { background:var(--amber); }
  .resp-bar-fill.baixo { background:var(--sage); }

  .resp-card-footer { display:flex; align-items:center; justify-content:space-between; }
  .resp-date { font-size:12px; color:var(--ink-light); }

  .view-toggle { display:flex; border:1px solid var(--border); border-radius:8px; overflow:hidden; }
  .view-toggle button {
    padding:7px 13px; border:none; background:transparent; cursor:pointer;
    font-size:13px; color:var(--ink-light); transition:all .15s;
  }
  .view-toggle button.active { background:var(--sage); color:white; }

  .resp-detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:18px; }
  .resp-detail-item { background:var(--cream); border-radius:8px; padding:12px 14px; }
  .resp-detail-lbl { font-size:11px; color:var(--ink-light); text-transform:uppercase; letter-spacing:.05em; margin-bottom:3px; }
  .resp-detail-val { font-size:14px; font-weight:500; color:var(--ink); }

  .resp-answers { display:flex; flex-direction:column; gap:8px; }
  .resp-answer-row {
    background:var(--cream); border-radius:8px; padding:10px 14px;
    display:flex; align-items:flex-start; gap:10px;
  }
  .resp-answer-q { font-size:13px; color:var(--ink-mid); flex:1; line-height:1.5; }
  .resp-answer-v {
    font-size:13px; font-weight:700; color:var(--sage);
    background:var(--sage-light); border-radius:6px; padding:2px 8px; flex-shrink:0;
  }

  @media (max-width: 900px) {
    .resp-summary { grid-template-columns: repeat(2,1fr); }
    .resp-cards   { grid-template-columns: 1fr; }
    .resp-detail-grid { grid-template-columns: 1fr; }
  }

  /* ── BULK IMPORT ────────────────────────────────────────────── */
  .drop-zone {
    border: 2px dashed var(--border); border-radius: 12px;
    padding: 40px 24px; text-align: center; cursor: pointer;
    transition: border-color .2s, background .2s; background: var(--cream);
  }
  .drop-zone:hover, .drop-zone.over { border-color: var(--sage); background: var(--sage-light); }
  .drop-zone-icon { font-size: 36px; margin-bottom: 10px; }
  .drop-zone-text { font-size: 15px; font-weight: 500; color: var(--ink-mid); }
  .drop-zone-sub  { font-size: 12px; color: var(--ink-light); margin-top: 4px; }

  .bulk-preview { max-height: 280px; overflow-y: auto; }
  .bulk-preview table { font-size: 12px; }
  .bulk-preview th { font-size: 11px; }
  .bulk-row-error td { background: #fff5f5; }
  .bulk-row-ok    td { }
  .bulk-cell-err  { color: var(--rose); font-weight: 600; }

  .bulk-summary {
    display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
  }
  .bulk-summary-item {
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 500;
  }
  .bulk-dot { width: 8px; height: 8px; border-radius: 50%; }

  /* ── CONSENT ────────────────────────────────────────────────── */
  .consent-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap;
  }
  .consent-pill.signed   { background: #e8f5e9; color: #4caf88; }
  .consent-pill.unsigned { background: #fee8e8; color: var(--rose); }
  .consent-pill.unknown  { background: var(--warm); color: var(--ink-light); }

  .terms-modal-student {
    display: flex; align-items: center; gap: 10px; padding: 10px 0;
    border-bottom: 1px solid var(--border);
  }
  .terms-modal-student:last-child { border-bottom: none; }
  .terms-check-group { display: flex; gap: 6px; flex-wrap: wrap; margin-left: auto; }
  .term-type-btn {
    padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600;
    border: 1.5px solid; cursor: pointer; transition: all .15s;
  }
  .term-type-btn.selected   { background: var(--sage); color: white; border-color: var(--sage); }
  .term-type-btn.unselected { background: transparent; color: var(--ink-mid); border-color: var(--border); }

  /* ── PWA / MOBILE ─────────────────────────────────────────── */
  @media (max-width: 768px) {
    .sidebar { display: none !important; }
    .main { margin-left: 0 !important; padding-top: calc(env(safe-area-inset-top) + 16px); padding-bottom: calc(88px + env(safe-area-inset-bottom)); }
    .notif-fab-wrap { bottom: calc(96px + env(safe-area-inset-bottom)); right: 16px; }
    .notif-fab { width: 46px; height: 46px; font-size: 18px; }
    .page-content { padding: 16px; }
    .notif-panel { right: 0; bottom: 60px; width: calc(100vw - 32px); max-width: 360px; }
  }

  /* Mobile bottom nav */
  .mobile-nav {
    display: none;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: calc(80px + env(safe-area-inset-bottom));
    background: var(--ink);
    border-top: 1px solid rgba(255,255,255,.08);
    z-index: 200;
    padding: 0 4px;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .mobile-nav-inner {
    display: flex;
    height: 80px;
    align-items: center;
    justify-content: space-around;
  }
  .mobile-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 10px 14px;
    border-radius: 12px;
    transition: background .15s;
    min-width: 60px;
    color: rgba(255,255,255,.45);
  }
  .mobile-nav-item.active { color: var(--sage-mid); background: rgba(157,208,206,.10); }
  .mobile-nav-item:active { background: rgba(255,255,255,.06); }
  .mobile-nav-icon { font-size: 26px; line-height: 1; }
  .mobile-nav-label { font-size: 11px; font-weight: 500; letter-spacing: .02em; line-height: 1; }

  @media (max-width: 768px) {
    .mobile-nav { display: flex; flex-direction: column; justify-content: flex-end; }
  }
`;


// ── OUVY LOGO ─────────────────────────────────────────────────────────────
function OuvyLogo({ size = 40 }) {
  return (
    <img
      src="/Logo.png"
      alt="Ouvy"
      style={{ height: size, width: "auto", display: "block" }}
    />
  );
}

// ── LOGIN PAGE ────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  async function handleLogin(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;
      onLogin(data.user);
    } catch (err) {
      setError(friendlyError(err.message));
    } finally {
      setLoading(false);
    }
  }

  function friendlyError(message = "") {
    if (message.includes("Invalid login credentials")) return "E-mail ou senha incorretos.";
    if (message.includes("Email not confirmed"))       return "Conta ainda não confirmada. Verifique seu e-mail.";
    if (message.includes("Too many requests"))         return "Muitas tentativas. Aguarde alguns minutos.";
    return "Ocorreu um erro. Tente novamente.";
  }

  return (
    <>
      <style>{css}</style>
      <div className="login-page">
        {/* Lado esquerdo — branding */}
        <div className="login-left">
          <img src="/Logo com frase.png" alt="Ouvy" style={{ height: 168, width: "auto", display: "block" }} />
        </div>

        {/* Lado direito — formulário */}
        <div className="login-right">
          <div className="login-box fade-in">
            <>
              <div className="login-title">Entrar na plataforma</div>
              <div className="login-subtitle">Use o e-mail e senha fornecidos pela Ouvy</div>
              {error && <div className="login-error">⚠️ {error}</div>}
              <form className="login-form" onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">E-mail</label>
                  <input
                    className="login-input" type="email" required autoFocus
                    placeholder="seu@email.com.br"
                    value={email} onChange={e => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Senha</label>
                  <input
                    className="login-input" type="password" required
                    placeholder="••••••••"
                    value={password} onChange={e => setPassword(e.target.value)}
                  />
                </div>
                <button className="login-btn" type="submit" disabled={loading}>
                  {loading ? "Entrando..." : "Entrar →"}
                </button>
              </form>
              <div className="login-footer">
                Problemas de acesso? Entre em contato com a Ouvy.
              </div>
            </>
          </div>
        </div>
      </div>
    </>
  );
}

// ── ICON HELPER ────────────────────────────────────────────────────────────
const Icon = ({ i }) => <span className="nav-icon">{i}</span>;

// ── TOAST ──────────────────────────────────────────────────────────────────
function Toast({ msg }) {
  return msg ? <div className="toast">✓ {msg}</div> : null;
}

// ── RISK BADGE ─────────────────────────────────────────────────────────────
function RiskBadge({ level }) {
  return (
    <span className={`risk ${level}`}>
      <span className="risk-dot" />
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────
function Dashboard({ students, alerts, onReadAlert }) {
  const [activeTab, setActiveTab] = useState("alunos");

  const alto  = students.filter(s => s.risk === "alto").length;
  const médio = students.filter(s => s.risk === "médio").length;
  const baixo = students.filter(s => s.risk === "baixo").length;
  const unread = alerts.filter(a => !a.read).length;

  const byYear = YEARS.map(y => ({ label: y, count: students.filter(s => s.year === y).length }))
    .filter(x => x.count > 0);
  const maxY = Math.max(...byYear.map(x => x.count), 1);

  const byClass = CLASSES.map(c => ({ label: `Turma ${c}`, count: students.filter(s => s.cls === c).length }))
    .filter(x => x.count > 0);
  const maxC = Math.max(...byClass.map(x => x.count), 1);

  const bySchool = SCHOOLS.map(sc => ({ label: sc.replace("E.M. ", "").replace("EMEF ", ""), count: students.filter(s => s.school === sc).length }));
  const maxS = Math.max(...bySchool.map(x => x.count), 1);

  const recentAlerts = alerts.slice(0, 5);

  return (
    <div className="fade-in">
      {/* STATS */}
      <div className="stats-grid">
        {[
          { label: "Total de Alunos",  val: students.length, cls: "total", icon: "👤" },
          { label: "Risco Alto",       val: alto,            cls: "alto",  icon: "🔴" },
          { label: "Risco Médio",      val: médio,           cls: "médio", icon: "🟡" },
          { label: "Risco Baixo",      val: baixo,           cls: "baixo", icon: "🟢" },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.cls}`}>
            <div className="stat-num">{s.val}</div>
            <div className="stat-label">{s.label}</div>
            <span className="stat-icon">{s.icon}</span>
          </div>
        ))}
      </div>

      {/* TABS */}
      <div className="tabs">
        {[["alunos","Por Aluno"],["serie","Por Série"],["turma","Por Turma"],["escola","Por Escola"]].map(([k,l]) => (
          <button key={k} className={`tab-btn ${activeTab === k ? "active" : ""}`} onClick={() => setActiveTab(k)}>{l}</button>
        ))}
      </div>

      <div className="grid-2">
        {/* LEFT */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              {activeTab === "alunos" ? "Alunos em Alerta" :
               activeTab === "serie"  ? "Distribuição por Série" :
               activeTab === "turma"  ? "Distribuição por Turma" :
                                        "Distribuição por Escola"}
            </span>
          </div>
          <div className="card-body">
            {activeTab === "alunos" && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Aluno</th><th>Risco</th><th>Alertas</th></tr>
                  </thead>
                  <tbody>
                    {students.filter(s => s.risk !== "baixo").length === 0 && (
                      <tr><td colSpan={3} style={{color:"var(--ink-light)",fontSize:13,textAlign:"center",padding:"24px 0"}}>
                        Nenhum aluno em alerta. Alunos com risco moderado ou alto nas respostas aparecerão aqui.
                      </td></tr>
                    )}
                    {students.filter(s => s.risk !== "baixo").map(s => (
                      <tr key={s.id}>
                        <td style={{fontWeight: 500}}>{s.name}</td>
                        <td><RiskBadge level={s.risk} /></td>
                        <td>
                          <span style={{background:"#FEE8E8",color:"var(--rose)",padding:"2px 8px",borderRadius:20,fontSize:12,fontWeight:600}}>
                            {s.alerts}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === "serie" && (
              <div className="bar-chart" style={{paddingTop:8}}>
                {byYear.map(r => (
                  <div key={r.label} className="bar-row">
                    <span className="bar-label">{r.label}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{width:`${(r.count/maxY)*100}%`,background:"var(--sage)"}} />
                    </div>
                    <span className="bar-val">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === "turma" && (
              <div className="bar-chart" style={{paddingTop:8}}>
                {byClass.map(r => (
                  <div key={r.label} className="bar-row">
                    <span className="bar-label">{r.label}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{width:`${(r.count/maxC)*100}%`,background:"var(--sky)"}} />
                    </div>
                    <span className="bar-val">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === "escola" && (
              <div className="bar-chart" style={{paddingTop:8}}>
                {bySchool.map(r => (
                  <div key={r.label} className="bar-row">
                    <span className="bar-label" style={{width:120,textAlign:"right",fontSize:11}}>{r.label}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{width:`${(r.count/maxS)*100}%`,background:"var(--amber)"}} />
                    </div>
                    <span className="bar-val">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT - ALERTS */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Alertas Recentes</span>
            <span style={{background:"#FEE8E8",color:"var(--rose)",padding:"2px 10px",borderRadius:20,fontSize:12,fontWeight:600}}>
              {unread} não lidos
            </span>
          </div>
          <div className="card-body" style={{padding:"8px 24px 16px"}}>
            {recentAlerts.length === 0 && (
              <div style={{color:"var(--ink-light)",fontSize:13,textAlign:"center",padding:"24px 0"}}>
                Nenhum alerta ainda. Respostas com risco moderado ou alto geram alertas automaticamente.
              </div>
            )}
            {recentAlerts.map(a => (
              <div key={a.id} className="alert-item" onClick={() => onReadAlert?.(a.id)} style={{cursor:"pointer"}} title="Marcar como lido">
                <div className={`alert-icon ${a.priority}`}>
                  {a.priority === "alta" ? "⚠️" : "ℹ️"}
                </div>
                <div className="alert-body">
                  <div className="alert-name">{a.student}</div>
                  <div className="alert-type">{a.type}</div>
                  <div className="alert-date">{a.date}</div>
                </div>
                {!a.read && <div className="unread-dot" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STUDENTS ───────────────────────────────────────────────────────────────
// ── CONSENT HELPERS ───────────────────────────────────────────────────────
const TERM_DEFS = [
  { key: "consent_signed", label: "Consentimento", desc: "Responsável assina",  type: "consent" },
  { key: "assent_signed",  label: "Assentimento",  desc: "Aluno assina",        type: "assent"  },
  { key: "terms_signed",   label: "Termos de Uso", desc: "Uso da plataforma",   type: "terms"   },
];

function ConsentPill({ signed }) {
  if (signed === true)  return <span className="consent-pill signed">✓ Assinado</span>;
  if (signed === false) return <span className="consent-pill unsigned">✗ Pendente</span>;
  return <span className="consent-pill unknown">— N/D</span>;
}

function hasMissingData(s) {
  return !s.name || !s.year || (!s.whatsapp_student && !s.whatsapp_parent);
}
function hasPendingTerms(s, types) {
  const map = { consent:"consent_signed", assent:"assent_signed", terms:"terms_signed" };
  return (types || ["consent","assent","terms"]).some(t => !s[map[t]]);
}

// ── TERMS MODAL ───────────────────────────────────────────────────────────
function TermsModal({ students, onClose, onSend }) {
  const TERM_MAP = { consent:"consent_signed", assent:"assent_signed", terms:"terms_signed" };
  const [selectedTypes, setSelectedTypes] = useState(["consent","assent","terms"]);
  const [selectedIds,   setSelectedIds]   = useState([]);
  const [sending,       setSending]       = useState(false);

  // Students with at least one selected term pending
  const pending = students.filter(s =>
    selectedTypes.some(t => !s[TERM_MAP[t]])
  );

  // Auto-select all pending on open / when types change
  useState(() => { setSelectedIds(pending.map(s => s.id)); });

  function toggleType(t) {
    setSelectedTypes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
  }

  function toggleStudent(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleSend() {
    if (!selectedIds.length || !selectedTypes.length) return;
    setSending(true);
    await onSend(selectedIds, selectedTypes);
    setSending(false);
  }

  const pendingFiltered = students.filter(s =>
    selectedTypes.some(t => !s[TERM_MAP[t]])
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:660}}>
        <div className="modal-header">
          <span className="modal-title">📋 Enviar Termos para Assinatura</span>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">

          {/* Quais termos enviar */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:600,color:"var(--ink-light)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>
              Quais termos enviar
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {TERM_DEFS.map(t => (
                <button
                  key={t.type}
                  className={`term-type-btn ${selectedTypes.includes(t.type) ? "selected" : "unselected"}`}
                  onClick={() => toggleType(t.type)}
                >
                  {t.label}
                  <span style={{marginLeft:5,opacity:.7,fontSize:10}}>({t.desc})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Lista de alunos com pendências */}
          <div style={{fontSize:12,fontWeight:600,color:"var(--ink-light)",textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>
            Alunos com pendências ({pendingFiltered.length})
            {pendingFiltered.length > 0 && (
              <button
                style={{marginLeft:12,fontSize:11,fontWeight:600,color:"var(--sage)",background:"none",border:"none",cursor:"pointer"}}
                onClick={() => setSelectedIds(
                  selectedIds.length === pendingFiltered.length ? [] : pendingFiltered.map(s => s.id)
                )}
              >
                {selectedIds.length === pendingFiltered.length ? "Desmarcar todos" : "Selecionar todos"}
              </button>
            )}
          </div>

          {pendingFiltered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🎉</div>
              <div className="empty-text">Todos os alunos já assinaram os termos selecionados!</div>
            </div>
          ) : (
            <div style={{maxHeight:320,overflowY:"auto",border:"1px solid var(--border)",borderRadius:10,padding:"0 16px"}}>
              {pendingFiltered.map(s => {
                const checked = selectedIds.includes(s.id);
                const noWa = !s.whatsapp_student && !s.whatsapp_parent;
                return (
                  <div key={s.id} className="terms-modal-student" style={{opacity: noWa ? .5 : 1}}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={noWa}
                      onChange={() => toggleStudent(s.id)}
                      style={{width:16,height:16,accentColor:"var(--sage)",flexShrink:0}}
                    />
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:500,fontSize:13}}>{s.name}</div>
                      <div style={{fontSize:11,color:"var(--ink-light)"}}>{s.year} {s.cls && `· Turma ${s.cls}`}</div>
                      {noWa && <div style={{fontSize:10,color:"var(--rose)"}}>⚠ Sem WhatsApp cadastrado</div>}
                    </div>
                    <div className="terms-check-group">
                      {TERM_DEFS.map(t => {
                        if (!selectedTypes.includes(t.type)) return null;
                        const signed = s[t.key];
                        return (
                          <span
                            key={t.key}
                            title={t.label}
                            className={`consent-pill ${signed ? "signed" : "unsigned"}`}
                            style={{fontSize:10}}
                          >
                            {signed ? "✓" : "✗"} {t.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{marginTop:16,padding:"12px 14px",background:"var(--sage-light)",borderRadius:10,fontSize:12,color:"var(--ink-mid)"}}>
            💬 Os termos serão enviados via <strong>WhatsApp</strong>. O Consentimento e os Termos de Uso vão para o responsável; o Assentimento vai para o aluno.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || selectedIds.length === 0 || selectedTypes.length === 0}
          >
            {sending ? "Enviando..." : `Enviar para ${selectedIds.length} aluno${selectedIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CSV column aliases → internal field names
const CSV_MAP = {
  nome: "name", name: "name",
  serie: "year", ano: "year", year: "year", série: "year",
  turma: "cls", class: "cls", cls: "cls",
  email_aluno: "email", email: "email",
  email_responsavel: "email_parent", email_parent: "email_parent", email_responsável: "email_parent",
  whatsapp_aluno: "whatsapp_student", whatsapp_student: "whatsapp_student",
  whatsapp_responsavel: "whatsapp_parent", whatsapp_parent: "whatsapp_parent", whatsapp_responsável: "whatsapp_parent",
};

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const raw = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g,"").toLowerCase().replace(/\s+/g,"_"));
  const headers = raw.map(h => CSV_MAP[h] || h);
  return lines.slice(1).map((line, i) => {
    // handle quoted commas
    const vals = [];
    let cur = "", inQ = false;
    for (const ch of line + ",") {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { vals.push(cur.trim()); cur = ""; }
      else { cur += ch; }
    }
    const row = Object.fromEntries(headers.map((h, j) => [h, vals[j] || ""]));
    row._line = i + 2;
    row._valid = !!row.name;
    row._error = !row.name ? "Nome obrigatório" : "";
    return row;
  });
}

function Students({ students, setStudents, setToast, institution }) {
  const [search, setSearch]               = useState("");
  const [filterYear, setFilterYear]       = useState("Todos");
  const [filterExtra, setFilterExtra]     = useState("all");
  const [showModal, setShowModal]         = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [bulkRows, setBulkRows]           = useState([]);
  const [bulkLoading, setBulkLoading]     = useState(false);
  const [detail, setDetail]               = useState(null);
  const [saving, setSaving]               = useState(false);
  const [dragOver, setDragOver]           = useState(false);
  const [form, setForm]                   = useState({
    name: "", year: YEARS[5], cls: "A",
    email: "", email_parent: "",
    whatsapp_student: "", whatsapp_parent: "",
  });

  function handleCSVFile(file) {
    if (!file || !file.name.endsWith(".csv")) {
      setToast("Por favor, selecione um arquivo .csv"); return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const rows = parseCSV(e.target.result);
      if (rows.length === 0) { setToast("Arquivo vazio ou sem dados válidos."); return; }
      setBulkRows(rows);
    };
    reader.readAsText(file, "UTF-8");
  }

  async function confirmBulkImport() {
    const valid = bulkRows.filter(r => r._valid);
    if (!valid.length) return;
    setBulkLoading(true);
    let ok = 0, fail = 0;
    for (const row of valid) {
      if (institution?.id) {
        const { data, error } = await createStudent({
          institution_id: institution.id,
          name: row.name,
          year: row.year || null,
          class: row.cls || null,
          email: row.email || null,
          email_parent: row.email_parent || null,
          whatsapp_student: row.whatsapp_student || null,
          whatsapp_parent: row.whatsapp_parent || null,
        });
        if (!error && data) {
          setStudents(prev => [{ ...data, cls: data.class, risk: "baixo", lastResp: "—", alerts: 0 }, ...prev]);
          ok++;
        } else { fail++; }
      } else {
        const localId = Date.now() + ok;
        const localRow = { ...row };
        setStudents(prev => [{
          id: localId, name: localRow.name, year: localRow.year, cls: localRow.cls,
          email: localRow.email, email_parent: localRow.email_parent,
          whatsapp_student: localRow.whatsapp_student, whatsapp_parent: localRow.whatsapp_parent,
          risk: "baixo", lastResp: "—", alerts: 0,
        }, ...prev]);
        ok++;
      }
    }
    setBulkLoading(false);
    setShowBulkModal(false);
    setBulkRows([]);
    setToast(`${ok} aluno${ok !== 1 ? "s" : ""} cadastrado${ok !== 1 ? "s" : ""}${fail ? ` · ${fail} com erro` : ""}!`);
  }

  const filtered = students.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    const matchYear   = filterYear === "Todos" || s.year === filterYear;
    let matchExtra = true;
    if (filterExtra === "missing_data")     matchExtra = hasMissingData(s);
    else if (filterExtra === "pending_all") matchExtra = hasPendingTerms(s);
    else if (filterExtra === "pending_consent") matchExtra = !s.consent_signed;
    else if (filterExtra === "pending_assent")  matchExtra = !s.assent_signed;
    else if (filterExtra === "pending_terms")   matchExtra = !s.terms_signed;
    return matchSearch && matchYear && matchExtra;
  });

  async function toggleConsent(studentId, field, currentVal) {
    const newVal = !currentVal;
    const { data, error } = await updateStudentConsent(studentId, field, newVal);
    if (error) { setToast("Erro ao atualizar termo."); return; }
    setStudents(prev => prev.map(s => s.id === studentId ? { ...s, ...data, cls: data.class } : s));
    if (detail?.id === studentId) setDetail(prev => ({ ...prev, [field]: newVal }));
    setToast(newVal ? "Termo marcado como assinado." : "Termo marcado como pendente.");
  }

  async function addStudent() {
    if (!form.name) return;
    setSaving(true);
    if (institution?.id) {
      const { data, error } = await createStudent({
        institution_id: institution.id,
        name: form.name,
        year: form.year,
        class: form.cls,
        email: form.email || null,
        email_parent: form.email_parent || null,
        whatsapp_student: form.whatsapp_student || null,
        whatsapp_parent: form.whatsapp_parent || null,
      });
      if (!error && data) {
        setStudents(prev => [{ ...data, cls: data.class, risk: "baixo", lastResp: "—", alerts: 0 }, ...prev]);
        setToast("Aluno cadastrado com sucesso!");
      } else {
        setToast("Erro ao cadastrar aluno. Verifique as configurações do Supabase.");
      }
    } else {
      // fallback sem Supabase
      setStudents(prev => [{
        id: Date.now(), ...form, risk: "baixo", lastResp: "—", alerts: 0,
      }, ...prev]);
      setToast("Aluno cadastrado (modo local).");
    }
    setSaving(false);
    setShowModal(false);
    setForm({ name:"", year: YEARS[5], cls:"A", email:"", email_parent:"", whatsapp_student:"", whatsapp_parent:"" });
  }

  async function deleteStudent(id) {
    if (institution?.id) await deactivateStudent(id);
    setStudents(prev => prev.filter(s => s.id !== id));
    if (detail?.id === id) setDetail(null);
    setToast("Aluno removido.");
  }

  if (detail) {
    const initials = detail.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
    return (
      <div className="fade-in">
        <div className="breadcrumb">
          <span onClick={() => setDetail(null)}>← Alunos</span>
          <span>/</span>
          <span style={{color:"var(--ink)"}}>{detail.name}</span>
        </div>
        <div className="detail-header">
          <div className="detail-avatar">{initials}</div>
          <div>
            <div className="detail-name">{detail.name}</div>
            <div className="detail-meta">{detail.year} · Turma {detail.cls}</div>
            <div style={{marginTop:8}}><RiskBadge level={detail.risk} /></div>
          </div>
        </div>
        <div className="grid-3">
          {[
            { label:"Série",         val: detail.year },
            { label:"Último Form.",  val: detail.lastResp },
            { label:"Alertas",       val: detail.alerts },
          ].map(k => (
            <div key={k.label} className="card" style={{padding:"18px 22px"}}>
              <div style={{fontSize:12,color:"var(--ink-light)",marginBottom:4}}>{k.label}</div>
              <div style={{fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:600}}>{k.val}</div>
            </div>
          ))}
        </div>
        <div className="card" style={{marginTop:16}}>
          <div className="card-header"><span className="card-title">Contatos</span></div>
          <div className="card-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[
                { label:"Email do aluno",       val: detail.email },
                { label:"Email do responsável", val: detail.email_parent },
                { label:"WhatsApp do aluno",    val: detail.whatsapp_student },
                { label:"WhatsApp responsável", val: detail.whatsapp_parent },
              ].map(c => (
                <div key={c.label}>
                  <div style={{fontSize:11,color:"var(--ink-light)",marginBottom:2}}>{c.label}</div>
                  <div style={{fontSize:14,color: c.val ? "var(--ink)" : "var(--ink-light)"}}>{c.val || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card" style={{marginTop:16}}>
          <div className="card-header"><span className="card-title">Termos de Aceite</span></div>
          <div className="card-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
              {TERM_DEFS.map(t => (
                <div key={t.key} style={{background:"var(--cream)",borderRadius:10,padding:"14px 16px"}}>
                  <div style={{fontSize:11,color:"var(--ink-light)",marginBottom:2}}>{t.desc}</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",marginBottom:10}}>{t.label}</div>
                  <ConsentPill signed={detail[t.key]} />
                  <button
                    style={{marginTop:10,display:"block",width:"100%",fontSize:11,background:"none",border:"1px solid var(--border)",borderRadius:6,padding:"5px 0",cursor:"pointer",color:"var(--ink-mid)"}}
                    onClick={() => toggleConsent(detail.id, t.key, detail[t.key])}
                  >
                    {detail[t.key] ? "↩ Marcar pendente" : "✓ Marcar assinado"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card" style={{marginTop:16}}>
          <div className="card-header"><span className="card-title">Histórico de Respostas</span></div>
          <div className="card-body">
            <div className="empty"><div className="empty-icon">📋</div><div className="empty-text">Nenhuma resposta registrada ainda.</div></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="filter-bar">
        <input className="filter-input" placeholder="🔍  Buscar aluno..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
          <option>Todos</option>
          {YEARS.map(y => <option key={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={filterExtra} onChange={e => setFilterExtra(e.target.value)}>
          <option value="all">Todos</option>
          <option value="missing_data">⚠️ Dados incompletos</option>
          <option value="pending_all">📋 Termos pendentes</option>
          <option value="pending_consent">— Consentimento pendente</option>
          <option value="pending_assent">— Assentimento pendente</option>
          <option value="pending_terms">— T. de Uso pendente</option>
        </select>
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button className="btn btn-outline" onClick={() => setShowTermsModal(true)}>📋 Enviar Termos</button>
          <button className="btn btn-outline" onClick={() => { setBulkRows([]); setShowBulkModal(true); }}>📤 Cadastro em Massa</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Novo Aluno</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Alunos Cadastrados</span>
          <span style={{fontSize:13,color:"var(--ink-light)"}}>{filtered.length} encontrado{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nome</th><th>Série</th><th>Turma</th><th>WhatsApp</th>
                <th title="Termo de Consentimento">Consent.</th>
                <th title="Termo de Assentimento">Assent.</th>
                <th title="Termos de Uso">T. Uso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="empty">
                    <div className="empty-icon">👤</div>
                    <div className="empty-text">
                      {filterExtra !== "all" ? "Nenhum aluno encontrado com esse filtro." : "Nenhum aluno cadastrado ainda."}
                    </div>
                    {filterExtra === "all" && (
                      <button className="btn btn-primary" style={{marginTop:12}} onClick={() => setShowModal(true)}>+ Cadastrar primeiro aluno</button>
                    )}
                  </div>
                </td></tr>
              ) : filtered.map(s => (
                <tr key={s.id} style={{cursor:"pointer"}} onClick={() => setDetail(s)}>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {hasMissingData(s) && <span title="Dados incompletos" style={{color:"var(--amber)",fontSize:12}}>⚠️</span>}
                      <span style={{fontWeight:500}}>{s.name}</span>
                    </div>
                  </td>
                  <td>{s.year}</td>
                  <td>{s.cls || s.class}</td>
                  <td style={{fontSize:13,color:"var(--ink-mid)"}}>{s.whatsapp_student || s.whatsapp_parent || "—"}</td>
                  <td><ConsentPill signed={s.consent_signed} /></td>
                  <td><ConsentPill signed={s.assent_signed} /></td>
                  <td><ConsentPill signed={s.terms_signed} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteStudent(s.id)}>Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:540}}>
            <div className="modal-header">
              <span className="modal-title">Cadastrar Aluno</span>
              <button className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Nome completo *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="Ex: Maria Silva" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Série</label>
                  <select className="form-select" value={form.year} onChange={e => setForm({...form,year:e.target.value})}>
                    {YEARS.map(y => <option key={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Turma</label>
                  <select className="form-select" value={form.cls} onChange={e => setForm({...form,cls:e.target.value})}>
                    {CLASSES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <p style={{fontSize:12,fontWeight:600,color:"var(--ink-light)",marginBottom:8,marginTop:4,textTransform:"uppercase",letterSpacing:".5px"}}>Contatos do Aluno</p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email do aluno</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form,email:e.target.value})} placeholder="aluno@email.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp do aluno</label>
                  <input className="form-input" type="tel" value={form.whatsapp_student} onChange={e => setForm({...form,whatsapp_student:e.target.value})} placeholder="55119XXXXXXXX" />
                </div>
              </div>
              <p style={{fontSize:12,fontWeight:600,color:"var(--ink-light)",marginBottom:8,marginTop:4,textTransform:"uppercase",letterSpacing:".5px"}}>Contatos do Responsável</p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email do responsável</label>
                  <input className="form-input" type="email" value={form.email_parent} onChange={e => setForm({...form,email_parent:e.target.value})} placeholder="responsavel@email.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp do responsável</label>
                  <input className="form-input" type="tel" value={form.whatsapp_parent} onChange={e => setForm({...form,whatsapp_parent:e.target.value})} placeholder="55119XXXXXXXX" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={addStudent} disabled={saving || !form.name}>
                {saving ? "Salvando..." : "Cadastrar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TERMS MODAL ── */}
      {showTermsModal && (
        <TermsModal
          students={students}
          onClose={() => setShowTermsModal(false)}
          onSend={async (studentIds, termTypes) => {
            try {
              const { data, error } = await sendTermsBatch({ studentIds, institutionId: institution?.id, termTypes });
              if (error) throw new Error(error.message || "Erro no envio");
              setToast(`Termos enviados para ${data?.sent ?? 0} aluno(s).`);
            } catch (e) {
              setToast("Erro ao enviar termos. Verifique a Edge Function.");
            }
            setShowTermsModal(false);
          }}
        />
      )}

      {/* ── BULK IMPORT MODAL ── */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:680}}>
            <div className="modal-header">
              <span className="modal-title">📤 Cadastro em Massa</span>
              <button className="btn-icon" onClick={() => setShowBulkModal(false)}>✕</button>
            </div>
            <div className="modal-body">

              {bulkRows.length === 0 ? (
                /* DROP ZONE */
                <div
                  className={`drop-zone ${dragOver ? "over" : ""}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleCSVFile(e.dataTransfer.files[0]); }}
                  onClick={() => document.getElementById("csv-file-input").click()}
                >
                  <div className="drop-zone-icon">📄</div>
                  <div className="drop-zone-text">Arraste o arquivo CSV aqui ou clique para selecionar</div>
                  <div className="drop-zone-sub">Apenas arquivos .csv · Codificação UTF-8</div>
                  <input
                    id="csv-file-input" type="file" accept=".csv"
                    style={{display:"none"}}
                    onChange={e => handleCSVFile(e.target.files[0])}
                  />
                </div>
              ) : (
                /* PREVIEW */
                <>
                  <div className="bulk-summary">
                    <div className="bulk-summary-item">
                      <div className="bulk-dot" style={{background:"var(--sage)"}} />
                      <span>{bulkRows.filter(r=>r._valid).length} válidos</span>
                    </div>
                    {bulkRows.filter(r=>!r._valid).length > 0 && (
                      <div className="bulk-summary-item">
                        <div className="bulk-dot" style={{background:"var(--rose)"}} />
                        <span>{bulkRows.filter(r=>!r._valid).length} com erro (serão ignorados)</span>
                      </div>
                    )}
                  </div>
                  <div className="bulk-preview card">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th><th>Nome</th><th>Série</th><th>Turma</th>
                          <th>WhatsApp Aluno</th><th>WhatsApp Resp.</th><th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkRows.map(r => (
                          <tr key={r._line} className={r._valid ? "bulk-row-ok" : "bulk-row-error"}>
                            <td style={{color:"var(--ink-light)",fontSize:11}}>{r._line}</td>
                            <td style={{fontWeight:500}}>{r.name || <span className="bulk-cell-err">—</span>}</td>
                            <td>{r.year || "—"}</td>
                            <td>{r.cls || "—"}</td>
                            <td style={{fontSize:12}}>{r.whatsapp_student || "—"}</td>
                            <td style={{fontSize:12}}>{r.whatsapp_parent || "—"}</td>
                            <td>
                              {r._valid
                                ? <span style={{color:"var(--sage)",fontSize:12,fontWeight:600}}>✓ OK</span>
                                : <span className="bulk-cell-err" style={{fontSize:12}}>✕ {r._error}</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{marginTop:12,display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button className="btn btn-outline" onClick={() => setBulkRows([])}>↩ Trocar arquivo</button>
                    <button
                      className="btn btn-primary"
                      onClick={confirmBulkImport}
                      disabled={bulkLoading || bulkRows.filter(r=>r._valid).length === 0}
                    >
                      {bulkLoading ? "Importando..." : `Importar ${bulkRows.filter(r=>r._valid).length} aluno${bulkRows.filter(r=>r._valid).length !== 1 ? "s" : ""}`}
                    </button>
                  </div>
                </>
              )}

              {/* FORMAT HINT */}
              <div style={{marginTop:20,padding:"14px 16px",background:"var(--sage-light)",borderRadius:10,fontSize:12,color:"var(--ink-mid)",lineHeight:1.7}}>
                <strong style={{color:"var(--sage)"}}>Estrutura esperada do CSV:</strong><br/>
                <code style={{fontSize:11}}>nome,serie,turma,email_aluno,email_responsavel,whatsapp_aluno,whatsapp_responsavel</code><br/>
                Apenas <strong>nome</strong> é obrigatório. Séries: "7º Ano", "8º Ano" etc. Turmas: A, B, C...
                WhatsApp no formato internacional: <code>5511999999999</code>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── FORMS ─────────────────────────────────────────────────────────────────
function ScalePreview({ escala, onClose, onLink }) {
  const [respostas, setRespostas] = useState({});
  const soma = Object.entries(respostas).reduce((acc, [idx, val]) => {
    const isInvertido = escala.invertidos && escala.invertidos.includes(Number(idx));
    const maxVal = escala.opcoes[0].valor; // highest option value
    return acc + (isInvertido ? maxVal - val : val);
  }, 0);
  const totalQ = escala.questoes.length;
  const respondidas = Object.keys(respostas).length;
  const resultado = respondidas === totalQ ? escala.pontuacao(soma) : null;
  const nivelColor = { alto: "var(--rose)", médio: "var(--amber)", baixo: "var(--sage)" };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:600,maxHeight:"90vh",overflowY:"auto"}}>
        <div className="modal-header">
          <span className="modal-title">{escala.title}</span>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{fontSize:13,color:"var(--ink-light)",marginBottom:4}}>{escala.desc}</p>
          <p style={{fontSize:12,color:"var(--ink-light)",marginBottom:20,fontStyle:"italic"}}>{escala.instrucao}</p>

          {escala.questoes.map((q, i) => (
            <div key={i} className="q-card" style={{marginBottom:16}}>
              <div className="q-num">Questão {i + 1}</div>
              <div className="q-text" style={{marginBottom:12}}>{q}</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {escala.opcoes.map(op => (
                  <button
                    key={op.valor}
                    onClick={() => setRespostas(prev => ({...prev, [i]: op.valor}))}
                    style={{
                      padding:"8px 14px", border:"1.5px solid",
                      borderColor: respostas[i] === op.valor ? "var(--sage)" : "var(--border)",
                      borderRadius:8, background: respostas[i] === op.valor ? "var(--sage-light)" : "white",
                      color: respostas[i] === op.valor ? "var(--sage)" : "var(--ink)",
                      fontWeight: respostas[i] === op.valor ? 600 : 400,
                      cursor:"pointer", textAlign:"left", fontSize:13,
                      transition:"all .15s",
                    }}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {resultado && (
            <div style={{
              marginTop:20, padding:"16px 20px", borderRadius:12,
              background: nivelColor[resultado.nivel] + "18",
              border: `2px solid ${nivelColor[resultado.nivel]}`,
            }}>
              <div style={{fontWeight:700,color:nivelColor[resultado.nivel],fontSize:16,marginBottom:4}}>
                {resultado.label}
              </div>
              <div style={{fontSize:13,color:"var(--ink-mid)"}}>{resultado.detalhe}</div>
            </div>
          )}

          {respondidas < totalQ && (
            <div style={{marginTop:16,fontSize:12,color:"var(--ink-light)",textAlign:"center"}}>
              {respondidas}/{totalQ} questões respondidas
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Fechar</button>
          <button className="btn btn-primary" onClick={() => { onClose(); onLink(); }}>🔗 Gerar Link</button>
        </div>
      </div>
    </div>
  );
}

function Forms({ setToast, students, institution }) {
  const [forms]                   = useState(INITIAL_FORMS);
  const [linkModal, setLinkModal] = useState(null);
  const [previewEscala, setPreviewEscala] = useState(null);
  const [sendModal, setSendModal] = useState(null);   // escala para envio em massa
  const [selectedIds, setSelectedIds] = useState([]); // alunos selecionados
  const [sending, setSending]     = useState(false);
  const fakeLink = id => `${window.location.origin}/f/${id}-${Math.random().toString(36).slice(2,8)}`;

  function copyLink(id) {
    navigator.clipboard.writeText(fakeLink(id)).catch(() => {});
    setToast("Link copiado para a área de transferência!");
    setLinkModal(null);
  }

  function toggleStudent(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  function toggleAll() {
    setSelectedIds(prev => prev.length === students.length ? [] : students.map(s => s.id));
  }

  async function handleSendBatch() {
    if (!selectedIds.length || !sendModal) return;
    setSending(true);
    try {
      if (institution?.id) {
        const { error } = await sendFormsBatch({
          escalaId: sendModal.escalaId,
          studentIds: selectedIds,
          institutionId: institution.id,
        });
        if (error) throw new Error(error.message);
        setToast(`✅ Formulário enviado para ${selectedIds.length} aluno(s)!`);
      } else {
        // fallback sem backend
        await new Promise(r => setTimeout(r, 1200));
        setToast(`✅ Formulário enviado para ${selectedIds.length} aluno(s)! (modo local)`);
      }
    } catch (e) {
      setToast("Erro ao enviar. Verifique as configurações da Edge Function.");
    }
    setSending(false);
    setSendModal(null);
    setSelectedIds([]);
  }

  const escalaIcon = { who5: "😊", swls: "⭐", gad7: "🧠", eps10: "⚡" };
  const escalaTag  = { who5: "OMS / WHO-5", swls: "SWLS-BP", gad7: "GAD-7", eps10: "EPS-10" };

  return (
    <div className="fade-in">
      <div style={{marginBottom:24}}>
        <p style={{fontSize:14,color:"var(--ink-light)"}}>
          Escalas clínicas validadas. Envie em massa para os alunos via WhatsApp e email.
        </p>
      </div>

      <div className="grid-2">
        {forms.map(f => {
          const escala = ESCALAS[f.escalaId];
          return (
            <div key={f.id} className="card">
              <div className="card-body">
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:28}}>{escalaIcon[f.escalaId]}</span>
                    <div>
                      <div style={{fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:500}}>{f.title}</div>
                      <div style={{
                        fontSize:11,marginTop:2,
                        background:"var(--sage-light)",color:"var(--sage)",
                        padding:"1px 8px",borderRadius:20,display:"inline-block",fontWeight:500
                      }}>{escalaTag[f.escalaId]}</div>
                    </div>
                  </div>
                  <span style={{
                    padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:500,
                    background: f.active ? "#E8F3E9" : "#F3F3F3",
                    color: f.active ? "var(--sage)" : "var(--ink-light)"
                  }}>{f.active ? "Ativo" : "Inativo"}</span>
                </div>

                <p style={{fontSize:12,color:"var(--ink-light)",marginBottom:14,lineHeight:1.5}}>
                  {escala.desc.slice(0,100)}...
                </p>

                <div style={{display:"flex",gap:20,marginBottom:16}}>
                  <div>
                    <span style={{fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:600}}>{escala.questoes.length}</span>
                    <span style={{fontSize:12,color:"var(--ink-light)",marginLeft:4}}>questões</span>
                  </div>
                  <div>
                    <span style={{fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:600}}>{f.responses}</span>
                    <span style={{fontSize:12,color:"var(--ink-light)",marginLeft:4}}>respostas</span>
                  </div>
                </div>

                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-outline btn-sm" onClick={() => setPreviewEscala(escala)}>
                    👁 Preview
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => setLinkModal(f)}>
                    🔗 Link
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => { setSendModal(f); setSelectedIds(students.map(s=>s.id)); }}>
                    📤 Enviar em massa
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* PREVIEW MODAL */}
      {previewEscala && (
        <ScalePreview
          escala={previewEscala}
          onClose={() => setPreviewEscala(null)}
          onLink={() => setLinkModal(forms.find(f => f.escalaId === previewEscala.id))}
        />
      )}

      {/* LINK MODAL */}
      {linkModal && (
        <div className="modal-overlay" onClick={() => setLinkModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:460}}>
            <div className="modal-header">
              <span className="modal-title">Link de Exemplo — {linkModal.title}</span>
              <button className="btn-icon" onClick={() => setLinkModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13,color:"var(--ink-light)",marginBottom:16}}>
                Este é um exemplo de como o link chega ao aluno. Para envios vinculados ao aluno use "Enviar em massa".
              </p>
              <div style={{
                background:"var(--sage-light)",border:"1px solid var(--sage-mid)",
                borderRadius:8,padding:"10px 14px",fontSize:13,
                fontFamily:"monospace",wordBreak:"break-all",color:"var(--ink-mid)"
              }}>
                {fakeLink(linkModal.id)}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setLinkModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={() => copyLink(linkModal.id)}>📋 Copiar Link</button>
            </div>
          </div>
        </div>
      )}

      {/* ENVIO EM MASSA MODAL */}
      {sendModal && (
        <div className="modal-overlay" onClick={() => { setSendModal(null); setSelectedIds([]); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:580,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
            <div className="modal-header">
              <span className="modal-title">📤 Enviar — {sendModal.title}</span>
              <button className="btn-icon" onClick={() => { setSendModal(null); setSelectedIds([]); }}>✕</button>
            </div>
            <div className="modal-body" style={{overflowY:"auto",flex:1}}>
              <p style={{fontSize:13,color:"var(--ink-light)",marginBottom:16}}>
                Selecione os alunos que vão receber este formulário. Cada aluno receberá um link único por WhatsApp e email.
              </p>

              {students.length === 0 ? (
                <div className="empty">
                  <div className="empty-icon">👤</div>
                  <div className="empty-text">Nenhum aluno cadastrado. Cadastre alunos primeiro.</div>
                </div>
              ) : (
                <>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <label style={{fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                      <input type="checkbox" checked={selectedIds.length === students.length} onChange={toggleAll} />
                      Selecionar todos ({students.length})
                    </label>
                    <span style={{fontSize:12,color:"var(--ink-light)"}}>{selectedIds.length} selecionado(s)</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflowY:"auto"}}>
                    {students.map(s => {
                      const hasWa = s.whatsapp_student || s.whatsapp_parent;
                      const hasEmail = s.email || s.email_parent;
                      return (
                        <label key={s.id} style={{
                          display:"flex",alignItems:"center",gap:12,
                          padding:"10px 14px",borderRadius:8,cursor:"pointer",
                          background: selectedIds.includes(s.id) ? "var(--sage-light)" : "var(--surface)",
                          border: `1.5px solid ${selectedIds.includes(s.id) ? "var(--sage-mid)" : "var(--border)"}`,
                          transition:"all .12s",
                        }}>
                          <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggleStudent(s.id)} />
                          <div style={{flex:1}}>
                            <div style={{fontWeight:500,fontSize:14}}>{s.name}</div>
                            <div style={{fontSize:12,color:"var(--ink-light)"}}>{s.year} · Turma {s.cls || s.class}</div>
                          </div>
                          <div style={{display:"flex",gap:6}}>
                            {hasWa    && <span title="WhatsApp" style={{fontSize:16}}>💬</span>}
                            {hasEmail && <span title="Email"    style={{fontSize:16}}>✉️</span>}
                            {!hasWa && !hasEmail && <span style={{fontSize:12,color:"var(--rose)"}}>sem contato</span>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setSendModal(null); setSelectedIds([]); }}>Cancelar</button>
              <button
                className="btn btn-primary"
                onClick={handleSendBatch}
                disabled={!selectedIds.length || sending}
              >
                {sending ? "Enviando..." : `Enviar para ${selectedIds.length} aluno(s) →`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── RESPONSES PAGE ────────────────────────────────────────────────────────
function Responses({ institution }) {
  const [responses, setResponses]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filterRisco, setFilterRisco]   = useState("all");
  const [filterEscala, setFilterEscala] = useState("all");
  const [filterYear, setFilterYear]     = useState("all");
  const [filterClass, setFilterClass]   = useState("all");
  const [viewMode, setViewMode]     = useState("cards");
  const [detail, setDetail]         = useState(null);

  useEffect(() => {
    if (!institution?.id) return;
    setLoading(true);
    listResponses(institution.id).then(({ data, error }) => {
      if (!error && data) setResponses(data);
      setLoading(false);
    });
  }, [institution]);

  const years   = [...new Set(responses.map(r => r.students?.year).filter(Boolean))].sort();
  const classes = [...new Set(responses.map(r => r.students?.class).filter(Boolean))].sort();
  const escalas = [...new Set(responses.map(r => r.escala_id).filter(Boolean))];

  const filtered = responses.filter(r => {
    const name = r.students?.name?.toLowerCase() || "";
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filterRisco  !== "all" && r.nivel_risco  !== filterRisco)          return false;
    if (filterEscala !== "all" && r.escala_id    !== filterEscala)         return false;
    if (filterYear   !== "all" && r.students?.year  !== filterYear)        return false;
    if (filterClass  !== "all" && r.students?.class !== filterClass)       return false;
    return true;
  });

  const counts = {
    total: filtered.length,
    alto:  filtered.filter(r => r.nivel_risco === "alto").length,
    medio: filtered.filter(r => r.nivel_risco === "médio").length,
    baixo: filtered.filter(r => r.nivel_risco === "baixo").length,
  };

  function formatDate(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric" });
  }

  function initials(name) {
    if (!name) return "?";
    return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  }

  function scorePercent(r) {
    const meta = ESCALA_META[r.escala_id];
    if (!meta || !r.pontuacao) return 0;
    return Math.round((r.pontuacao / meta.max) * 100);
  }

  // ── Detail Modal ──────────────────────────────────────────────
  function DetailModal({ r, onClose }) {
    const meta    = ESCALA_META[r.escala_id] || {};
    const escala  = ESCALAS[r.escala_id];
    const resps   = Array.isArray(r.respostas) ? r.respostas : [];
    const pct     = scorePercent(r);

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{maxWidth:620}} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <div className="modal-title">{r.students?.name || "Aluno"}</div>
              <div style={{fontSize:13,color:"var(--ink-light)",marginTop:3}}>
                {r.students?.year} {r.students?.class} · {formatDate(r.answered_at)}
              </div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--ink-light)"}}>✕</button>
          </div>
          <div className="modal-body">
            {/* Summary grid */}
            <div className="resp-detail-grid">
              <div className="resp-detail-item">
                <div className="resp-detail-lbl">Escala</div>
                <div className="resp-detail-val">{meta.icon} {meta.title} — {meta.subtitle}</div>
              </div>
              <div className="resp-detail-item">
                <div className="resp-detail-lbl">Pontuação</div>
                <div className="resp-detail-val">{r.pontuacao ?? "—"} / {meta.max ?? "—"} ({pct}%)</div>
              </div>
              <div className="resp-detail-item">
                <div className="resp-detail-lbl">Resultado</div>
                <div className="resp-detail-val">{r.label_resultado || "—"}</div>
              </div>
              <div className="resp-detail-item">
                <div className="resp-detail-lbl">Nível de risco</div>
                <div className="resp-detail-val">
                  <span className={`risk ${r.nivel_risco}`}>
                    <span className="risk-dot" />
                    {r.nivel_risco?.charAt(0).toUpperCase() + r.nivel_risco?.slice(1) || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Score bar */}
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--ink-light)",marginBottom:6}}>
                <span>Pontuação na escala</span><span>{pct}%</span>
              </div>
              <div className="resp-bar-track" style={{height:10}}>
                <div className={`resp-bar-fill ${r.nivel_risco}`} style={{width:`${pct}%`}} />
              </div>
            </div>

            {/* Individual answers */}
            {escala && resps.length > 0 && (
              <>
                <div style={{fontSize:13,fontWeight:600,color:"var(--ink-mid)",marginBottom:10,textTransform:"uppercase",letterSpacing:".05em"}}>
                  Respostas individuais
                </div>
                <div className="resp-answers">
                  {escala.questoes.map((q, i) => {
                    const val = resps[i];
                    const opt = escala.opcoes?.find(o => o.valor === val);
                    return (
                      <div key={i} className="resp-answer-row">
                        <span style={{fontSize:11,fontWeight:700,color:"var(--ink-light)",minWidth:20}}>{i+1}.</span>
                        <span className="resp-answer-q">{q}</span>
                        <span className="resp-answer-v">{opt ? opt.label : val ?? "—"}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  if (loading) return (
    <div className="empty"><div className="empty-icon">⏳</div><div className="empty-text">Carregando respostas…</div></div>
  );

  return (
    <div className="fade-in">
      {/* Summary stats */}
      <div className="resp-summary">
        {[
          { key:"total", label:"Total de respostas", color:"var(--sage)",  val: counts.total },
          { key:"alto",  label:"Risco alto",          color:"var(--rose)",  val: counts.alto  },
          { key:"medio", label:"Risco moderado",       color:"var(--amber)", val: counts.medio },
          { key:"baixo", label:"Bom bem-estar",        color:"#4caf88",      val: counts.baixo },
        ].map(s => (
          <div key={s.key} className={`resp-stat ${s.key}`}>
            <div className="resp-stat-num" style={{color:s.color}}>{s.val}</div>
            <div className="resp-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <input
          className="filter-input" placeholder="🔍 Buscar aluno…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{flex:1, minWidth:180}}
        />
        <select className="filter-select" value={filterRisco} onChange={e => setFilterRisco(e.target.value)}>
          <option value="all">Todos os riscos</option>
          <option value="alto">🔴 Risco alto</option>
          <option value="médio">🟡 Risco moderado</option>
          <option value="baixo">🟢 Bom bem-estar</option>
        </select>
        <select className="filter-select" value={filterEscala} onChange={e => setFilterEscala(e.target.value)}>
          <option value="all">Todas as escalas</option>
          {escalas.map(e => (
            <option key={e} value={e}>{ESCALA_META[e]?.title || e}</option>
          ))}
        </select>
        <select className="filter-select" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
          <option value="all">Todos os anos</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="all">Todas as turmas</option>
          {classes.map(c => <option key={c} value={c}>Turma {c}</option>)}
        </select>
        <div className="view-toggle">
          <button className={viewMode==="cards"?"active":""} onClick={() => setViewMode("cards")} title="Cards">⊞</button>
          <button className={viewMode==="table"?"active":""} onClick={() => setViewMode("table")} title="Tabela">☰</button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="empty">
          <div className="empty-icon">📭</div>
          <div className="empty-text">
            {responses.length === 0
              ? "Nenhuma resposta recebida ainda. Envie formulários para os alunos."
              : "Nenhuma resposta encontrada com os filtros selecionados."}
          </div>
        </div>
      )}

      {/* Cards view */}
      {viewMode === "cards" && filtered.length > 0 && (
        <div className="resp-cards">
          {filtered.map(r => {
            const meta = ESCALA_META[r.escala_id] || {};
            const pct  = scorePercent(r);
            const ini  = initials(r.students?.name);
            return (
              <div key={r.id} className="resp-card" onClick={() => setDetail(r)}>
                <div className="resp-card-top">
                  <div className="resp-avatar">{ini}</div>
                  <div>
                    <div className="resp-student-name">{r.students?.name || "—"}</div>
                    <div className="resp-student-meta">{r.students?.year} {r.students?.class && `· Turma ${r.students.class}`}</div>
                  </div>
                  <div className="resp-escala-badge">{meta.icon} {meta.title}</div>
                </div>

                <div className="resp-score-area">
                  <div className="resp-score-header">
                    <span className="resp-score-label">{r.label_resultado || meta.subtitle}</span>
                    <span className="resp-score-val">{r.pontuacao ?? "—"}/{meta.max ?? "?"}</span>
                  </div>
                  <div className="resp-bar-track">
                    <div className={`resp-bar-fill ${r.nivel_risco}`} style={{width:`${pct}%`}} />
                  </div>
                </div>

                <div className="resp-card-footer">
                  <span className="resp-date">📅 {formatDate(r.answered_at)}</span>
                  <span className={`risk ${r.nivel_risco}`}>
                    <span className="risk-dot" />
                    {r.nivel_risco}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table view */}
      {viewMode === "table" && filtered.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Aluno</th>
                  <th>Ano / Turma</th>
                  <th>Escala</th>
                  <th>Pontuação</th>
                  <th>Resultado</th>
                  <th>Risco</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const meta = ESCALA_META[r.escala_id] || {};
                  const pct  = scorePercent(r);
                  return (
                    <tr key={r.id} style={{cursor:"pointer"}} onClick={() => setDetail(r)}>
                      <td>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div className="resp-avatar" style={{width:32,height:32,fontSize:12}}>{initials(r.students?.name)}</div>
                          <span style={{fontWeight:500}}>{r.students?.name || "—"}</span>
                        </div>
                      </td>
                      <td>{r.students?.year} {r.students?.class && `· ${r.students.class}`}</td>
                      <td>{meta.icon} {meta.title}</td>
                      <td>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div className="resp-bar-track" style={{width:60}}>
                            <div className={`resp-bar-fill ${r.nivel_risco}`} style={{width:`${pct}%`}} />
                          </div>
                          <span style={{fontSize:12,color:"var(--ink-light)"}}>{r.pontuacao}/{meta.max}</span>
                        </div>
                      </td>
                      <td style={{fontSize:13}}>{r.label_resultado || "—"}</td>
                      <td><span className={`risk ${r.nivel_risco}`}><span className="risk-dot"/>{r.nivel_risco}</span></td>
                      <td style={{fontSize:13,color:"var(--ink-light)"}}>{formatDate(r.answered_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && <DetailModal r={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Settings({ setToast }) {
  const [toggles, setToggles] = useState({
    emailAlerts: true, smsAlerts: false,
    weeklyReport: true, anonymousMode: false,
    autoRisk: true, twoFactor: false,
  });
  const [profile, setProfile] = useState({ name:"Allana Mello", email:"allanamello.psi@gmail.com", school: SCHOOLS[0] });
  const [activeTab, setActiveTab] = useState("conta");

  function toggle(k) { setToggles(t => ({...t,[k]:!t[k]})); setToast("Configuração salva."); }

  const settingItems = [
    { k:"emailAlerts",   label:"Alertas por E-mail",    desc:"Receba notificações de alto risco no seu e-mail" },
    { k:"smsAlerts",     label:"Alertas por SMS",        desc:"Receba SMS quando houver alertas críticos" },
    { k:"weeklyReport",  label:"Relatório Semanal",      desc:"Receba um resumo automático toda segunda-feira" },
    { k:"autoRisk",      label:"Classificação Automática de Risco", desc:"Calcule o nível de risco automaticamente com base nas respostas" },
    { k:"anonymousMode", label:"Modo Anônimo",           desc:"Oculte os nomes dos alunos nos relatórios exportados" },
    { k:"twoFactor",     label:"Autenticação em 2 Fatores", desc:"Adicione uma camada extra de segurança ao login" },
  ];

  return (
    <div className="fade-in">
      <div className="tabs">
        {[["conta","Conta"],["notificacoes","Notificações"],["seguranca","Segurança"],["escola","Escolas"]].map(([k,l]) => (
          <button key={k} className={`tab-btn ${activeTab===k?"active":""}`} onClick={() => setActiveTab(k)}>{l}</button>
        ))}
      </div>

      {activeTab === "conta" && (
        <div className="card" style={{maxWidth:600}}>
          <div className="card-header"><span className="card-title">Perfil do Usuário</span></div>
          <div className="card-body">
            <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24,paddingBottom:20,borderBottom:"1px solid var(--border)"}}>
              <div style={{width:64,height:64,borderRadius:"50%",background:"var(--sage-light)",border:"2px solid var(--sage-mid)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Fraunces',serif",fontSize:24,color:"var(--sage)"}}>
                AM
              </div>
              <div>
                <div style={{fontFamily:"'Fraunces',serif",fontSize:18}}>{profile.name}</div>
                <div style={{fontSize:13,color:"var(--ink-light)",marginTop:2}}>Psicóloga Responsável</div>
                <button className="btn btn-outline btn-sm" style={{marginTop:8}}>Alterar foto</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Nome completo</label>
              <input className="form-input" value={profile.name} onChange={e=>setProfile({...profile,name:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input className="form-input" value={profile.email} onChange={e=>setProfile({...profile,email:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Escola principal</label>
              <select className="form-select" value={profile.school} onChange={e=>setProfile({...profile,school:e.target.value})}>
                {SCHOOLS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={() => setToast("Perfil salvo!")}>Salvar Alterações</button>
          </div>
        </div>
      )}

      {activeTab === "notificacoes" && (
        <div className="card" style={{maxWidth:600}}>
          <div className="card-header"><span className="card-title">Preferências de Notificação</span></div>
          <div className="card-body">
            {settingItems.slice(0,3).map(s => (
              <div key={s.k} className="setting-row">
                <div className="setting-info"><h4>{s.label}</h4><p>{s.desc}</p></div>
                <button className={`toggle ${toggles[s.k]?"on":"off"}`} onClick={() => toggle(s.k)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "seguranca" && (
        <div className="card" style={{maxWidth:600}}>
          <div className="card-header"><span className="card-title">Segurança & Privacidade</span></div>
          <div className="card-body">
            {settingItems.slice(3).map(s => (
              <div key={s.k} className="setting-row">
                <div className="setting-info"><h4>{s.label}</h4><p>{s.desc}</p></div>
                <button className={`toggle ${toggles[s.k]?"on":"off"}`} onClick={() => toggle(s.k)} />
              </div>
            ))}
            <div style={{marginTop:20}}>
              <button className="btn btn-outline">🔒 Alterar Senha</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "escola" && (
        <div className="card" style={{maxWidth:600}}>
          <div className="card-header">
            <span className="card-title">Escolas Cadastradas</span>
            <button className="btn btn-primary btn-sm" onClick={() => setToast("Em breve: adicionar escola!")}>+ Adicionar</button>
          </div>
          <div className="card-body" style={{padding:0}}>
            {SCHOOLS.map((sc,i) => (
              <div key={sc} style={{padding:"14px 24px",borderBottom: i < SCHOOLS.length-1 ? "1px solid var(--border)" : "none",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontWeight:500,fontSize:14}}>{sc}</div>
                  <div style={{fontSize:12,color:"var(--ink-light)",marginTop:2}}>
                    {INITIAL_STUDENTS.filter(s=>s.school===sc).length} alunos cadastrados
                  </div>
                </div>
                <button className="btn btn-outline btn-sm">Editar</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── APP ────────────────────────────────────────────────────────────────────
export default function App() {
  const [authState, setAuthState]     = useState("loading"); // "loading"|"login"|"app"
  const [currentUser, setCurrentUser] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [page, setPage]               = useState("dashboard");
  const [students, setStudents]       = useState([]);
  const [responses, setResponses]     = useState([]);
  const [readAlerts, setReadAlerts]   = useState(loadReadAlerts);
  const [toast, setToast]             = useState("");
  const [notifOpen, setNotifOpen]     = useState(false);

  // Check if user is already signed in on load + reactive session listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setCurrentUser(session.user);
        setAuthState("app");
      } else {
        setAuthState("login");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setCurrentUser(session.user);
        setAuthState("app");
      } else {
        setCurrentUser(null);
        setAuthState("login");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadResponses = useCallback(async (instId) => {
    const { data, error } = await listResponses(instId);
    if (!error && data) setResponses(data);
  }, []);

  // Load institution + students from Supabase when logged in
  useEffect(() => {
    if (!currentUser) return;
    async function load() {
      const { institution: inst } = await getOrCreateInstitution(currentUser);
      if (inst) {
        setInstitution(inst);
        loadResponses(inst.id);
        const { data } = await listStudents(inst.id);
        if (data) setStudents(data.map(s => ({
          ...s,
          cls: s.class,
          school: inst.name,
          risk: "baixo",
          lastResp: "—",
          alerts: 0,
        })));
      }
    }
    load();
  }, [currentUser, loadResponses]);

  // Recarrega respostas ao voltar para o dashboard/alunos (novas respostas → novos alertas)
  useEffect(() => {
    if (!institution?.id) return;
    if (page === "dashboard" || page === "students") loadResponses(institution.id);
  }, [page, institution?.id, loadResponses]);

  // Alunos enriquecidos com risco/alertas reais + lista de alertas
  const enrichedStudents = useMemo(() => enrichStudents(students, responses), [students, responses]);
  const alerts           = useMemo(() => buildAlerts(responses, readAlerts), [responses, readAlerts]);

  const markAlertRead = useCallback((id) => {
    setReadAlerts(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev); next.add(id); persistReadAlerts(next); return next;
    });
  }, []);
  const markAllAlertsRead = useCallback(() => {
    setReadAlerts(prev => {
      const next = new Set(prev); alerts.forEach(a => next.add(a.id)); persistReadAlerts(next); return next;
    });
  }, [alerts]);

  const handleLogin = useCallback((user) => {
    setCurrentUser(user);
    setAuthState("app");
  }, []);

  const handleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setAuthState("login");
    setPage("dashboard");
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Loading state
  if (authState === "loading") {
    return (
      <>
        <style>{css}</style>
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--ink)"}}>
          <div style={{textAlign:"center"}}>
            <OuvyLogo size={100} />
            <div style={{color:"var(--ink-light)",fontSize:13,marginTop:16}}>Carregando...</div>
          </div>
        </div>
      </>
    );
  }

  // Login state
  if (authState === "login") {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Get display name from Supabase user
  const userName = currentUser?.email?.split("@")[0] || "Usuário";
  const userInitials = userName.slice(0,2).toUpperCase();

  const NAV = [
    { id:"dashboard", label:"Dashboard",    icon:"📊" },
    { id:"students",   label:"Alunos",        icon:"👥" },
    { id:"forms",      label:"Formulários",   icon:"📋" },
    { id:"responses",  label:"Respostas",     icon:"📝" },
    { id:"settings",   label:"Configurações", icon:"⚙️" },
  ];


  const unreadCount = alerts.filter(a => !a.read).length;

  return (
    <>
      <style>{css}</style>
      <div className="app">
        {/* SIDEBAR */}
        <aside className="sidebar">
          <div className="sidebar-logo">
            <img src="/Logo.png" alt="Ouvy" style={{ height: 29, width: "auto", display: "block" }} />
          </div>
          <nav className="sidebar-nav">
            {NAV.map(n => (
              <button key={n.id} className={`nav-item ${page===n.id?"active":""}`} onClick={() => setPage(n.id)}>
                <Icon i={n.icon} />
                {n.label}
                {n.id === "dashboard" && unreadCount > 0 && (
                  <span style={{marginLeft:"auto",background:"var(--rose)",color:"white",borderRadius:20,padding:"1px 7px",fontSize:11,fontWeight:700}}>
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div className="user-pill" style={{cursor:"default"}}>
              <div className="user-avatar">{userInitials}</div>
              <div style={{flex:1,minWidth:0}}>
                <div className="user-name" style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userName}</div>
                <div className="user-role">Psicóloga</div>
              </div>
              <button
                onClick={handleLogout}
                title="Sair"
                style={{background:"none",border:"none",cursor:"pointer",color:"var(--ink-light)",fontSize:16,padding:"2px 4px",flexShrink:0}}
              >⎋</button>
            </div>
          </div>
        </aside>

        {/* MOBILE BOTTOM NAV */}
        <nav className="mobile-nav">
          <div className="mobile-nav-inner">
            {NAV.map(n => (
              <button
                key={n.id}
                className={`mobile-nav-item${page === n.id ? " active" : ""}`}
                onClick={() => setPage(n.id)}
              >
                <span className="mobile-nav-icon">{n.icon}</span>
                <span className="mobile-nav-label">{n.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* MAIN */}
        <main className="main">
          <div className="content">
            {page === "dashboard" && <Dashboard  students={enrichedStudents} alerts={alerts} onReadAlert={markAlertRead} />}
            {page === "students"  && <Students   students={enrichedStudents} setStudents={setStudents} setToast={setToast} institution={institution} />}
            {page === "forms"     && <Forms      setToast={setToast} students={enrichedStudents} institution={institution} />}
            {page === "responses" && <Responses  institution={institution} />}
            {page === "settings"  && <Settings   setToast={setToast} />}
          </div>
        </main>

        {/* NOTIFICATION FAB */}
        <div className="notif-fab-wrap">
          {notifOpen && (
            <div className="notif-panel">
              <div className="notif-panel-header">
                <span className="notif-panel-title">Notificações</span>
                {unreadCount > 0 && (
                  <button onClick={markAllAlertsRead} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"var(--rose)",fontWeight:600}}>
                    {unreadCount} não lidas · marcar todas
                  </button>
                )}
              </div>
              <div className="notif-panel-body">
                {alerts.length === 0 ? (
                  <div className="notif-empty">Nenhuma notificação</div>
                ) : alerts.map(a => (
                  <div key={a.id} className="notif-item" onClick={() => markAlertRead(a.id)} style={{cursor:"pointer"}}>
                    <span className={`notif-item-dot ${a.read ? "read" : "unread"}`} />
                    <div>
                      <div className="notif-item-name">{a.student}</div>
                      <div className="notif-item-type">{a.type}</div>
                      <div className="notif-item-date">{a.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            className="notif-fab"
            onClick={() => setNotifOpen(o => !o)}
            title="Notificações"
          >
            🔔
            {unreadCount > 0 && (
              <span className="notif-fab-count">{unreadCount}</span>
            )}
          </button>
        </div>
      </div>

      <Toast msg={toast} />
    </>
  );
}
